/**
 * Lógica compartida del bloque visual de cobros por etapas (proforma → etapa 1 / etapa 2).
 * Usado por CollectionSummary.vue (y no debe mezclarse con FinancialSummary salvo vía ese componente).
 */
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import {
  resolveProformContainerCountFromRecord,
  showShippingContainerPickDialog,
  patchShippingItemQtyToMatchContainerPick,
} from "@/utils/shippingContainerSlots.js";
import {
  calendarTodayLocalYyyyMmDd,
  formatDateSync,
  toYyyyMmDdSync,
} from "@/utils/dateFormatter";
import { buildStage1AutoShipSplitContext } from "@/components/gotrading/utils/stage1CollectAmountSplit.js";

export default {
  watch: {
    /** Al entrar a la operación (los datos llegan por props, no en mounted). */
    proformContainerMismatch: {
      immediate: true,
      handler(mismatch) {
        if (!mismatch || this.containerMismatchPromptShown) return;
        this.containerMismatchPromptShown = true;
        this.$nextTick(() => {
          this.promptProformContainerMismatch(mismatch);
        });
      },
    },
    "itemData._id": function () {
      this.containerMismatchPromptShown = false;
    },
  },
  data() {
    return {
      redistributeInProgress: false,
      /** El aviso de contenedores inconsistentes se muestra una sola vez por operación. */
      containerMismatchPromptShown: false,
      fixingProformContainers: false,
      showAddShippingFromProformModal: false,
      addShippingFromProformEditingItem: null,
      quickCollectAllowAmountEdit: false,
      quickCollectAmountMax: null,
      savingProformAdvance: false,
      showProformAdvanceModal: false,
      /** Borrador % adelanto proveedor por shippingId (etapa 2 → advance_sp). */
      inlineSupplierAdvanceDraft: {},
      savingSupplierAdvanceShippingId: null,
      showAddCollectionModal: false,
      /** Solo al abrir cobro desde el botón «Quick collect» (rayo): calculadora por contenedor en el modal. */
      showCollectContainerCalculator: false,
      showQuickShippingShipperModal: false,
      parentID: null,
      parentModel: null,
      extraParams: {},
      cachedLists: [],
      showCollectionDetailsModal: false,
      loadingCollectionDetails: false,
      collectionDetails: null,
      showShipperPricesModal: false,
      shipperPricesModalShippingId: "",
      shipperPricesModalShippingName: "",
      showShipperInvoiceQtyModal: false,
      shipperInvoiceQtyModalShippingId: "",
      shipperInvoiceQtyModalShippingName: "",
      showInvoiceUnitQtyModal: false,
      invoiceUnitQtyModalShippingId: "",
      invoiceUnitQtyModalShippingName: "",
      showAutoloadExpensesModal: false,
      autoloadExpensesModalShippingId: "",
      autoloadExpensesModalShippingName: "",
      showAutoloadPackingListModal: false,
      autoloadPackingListModalShippingId: "",
      autoloadPackingListModalShippingName: "",
      /** Embarque en vista compacta etapa 2: detalle abierto por `shippingId`. */
      shippingStage2CompactExpanded: {},
      /** Durante drag & drop: resaltar zonas donde se puede soltar. */
      stagesDndActive: false,
      /** `proform_only` | `ship_advance` | `shipping_only` si el arrastre es un cobro. */
      stagesDndCollectKind: null,
      /** `proform_to_ship` | `shipping_to_proform` si el arrastre es una nota comercial. */
      stagesDndNotaMode: null,
      /** true si el drag empezó en etapa 2 (cobro embarque / NC en embarque). */
      stagesDndFromStage2: false,
      /** Cursor sobre la zona «volver a proforma» (refuerzo del borde punteado). */
      stagesDndReturnHover: false,
    };
  },
  computed: {
    collectionStagesVisual() {
      return this.financialData?.collectionStagesVisual || null;
    },
    listsForModals() {
      return this.lists && this.lists.length ? this.lists : this.cachedLists;
    },
    /**
     * Opciones de condición de pago (lista paymentPayable) para el modal de adelanto.
     * @returns {Array<{_id: string, label: string}>}
     */
    proformPayableOptions() {
      const list = (this.listsForModals || []).find((l) => l && l.type === "paymentPayable");
      const values = (list && Array.isArray(list.values) && list.values) || [];
      return values
        .filter((v) => v && v._id)
        .map((v) => ({ _id: String(v._id), label: String(v.label || "") }));
    },
    /** Condición de pago vigente de la proforma; viene como id o ya poblada. */
    proformPayableCurrentId() {
      const raw = this.itemData && this.itemData.payable;
      if (!raw) return "";
      return String(raw._id || raw);
    },
    /** Reparto automático etapa 1 → único embarque si el monto supera el adelanto pendiente (QuickAddCollectModal). */
    quickCollectStage1AutoShipSplitContext() {
      return buildStage1AutoShipSplitContext({
        extraParams: this.extraParams,
        parentModel: this.parentModel || this.modelName,
        collectionStagesVisual: this.collectionStagesVisual,
        amountMax: this.quickCollectAmountMax,
      });
    },
    stage1DisplayTotalProform() {
      const s = this.collectionStagesVisual && this.collectionStagesVisual.stage1;
      if (!s) return 0;
      const t = Number(s.totalProform);
      if (Number.isFinite(t)) return t;
      const fp = this.financialData && this.financialData.totalProform;
      return Number.isFinite(Number(fp)) ? Number(fp) : 0;
    },
    /** NC cliente aplicada solo a proforma (reduce pendiente de adelanto). */
    stage1ClientNotaCreditProformApplied() {
      const s = this.collectionStagesVisual && this.collectionStagesVisual.stage1;
      const v = Number(s && s.clientNotaCreditProformApplied);
      return Number.isFinite(v) ? v : 0;
    },
    /** ND cliente aplicada solo a proforma (aumenta pendiente de adelanto). */
    stage1ClientNotaDebitProformApplied() {
      const s = this.collectionStagesVisual && this.collectionStagesVisual.stage1;
      const v = Number(s && s.clientNotaDebitProformApplied);
      return Number.isFinite(v) ? v : 0;
    },
    collectionStage1ShowNotaImpact() {
      return (
        this.stage1ClientNotaCreditProformApplied > 0.01 || this.stage1ClientNotaDebitProformApplied > 0.01
      );
    },
    proformContainerCountRounded() {
      return Math.round(this.resolveProformContainerCount());
    },
    /**
     * Contenedores de toda la operación: unión de las listas de empaque de sus
     * embarques, sin repetir. Vacío mientras ningún embarque tenga contenedor cargado.
     * @returns {string[]}
     */
    proformContainerNumbers() {
      const ships = this.collectionStagesVisual?.stage2Shippings || [];
      const seen = new Set();
      const out = [];
      ships.forEach((ship) => {
        this.shippingContainerNumbers(ship).forEach((text) => {
          const key = text.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          out.push(text);
        });
      });
      return out;
    },
    /** Los contenedores de la operación separados por coma. */
    proformContainerNumbersText() {
      return this.proformContainerNumbers.join(", ");
    },
    /** Cobros en etapa 2 (embarque) que se pueden devolver a solo-proforma. */
    stage2HasCollectChipsForDnD() {
      const ships = this.collectionStagesVisual?.stage2Shippings || [];
      return ships.some((s) => {
        const adv = (s.advanceRows || []).some((r) => Number(r.amount) > 0.005);
        const inv = (s.shippingOnlyRows || []).some((r) => Number(r.amount) > 0.005);
        return adv || inv;
      });
    },
    /**
     * Cobros del adelanto (etapa 1) que ya están asignados a un embarque.
     * Etapa 1 solo lista líneas solo-proforma, así que cuando todo el adelanto
     * quedó repartido en embarques no se veía ningún cobro detrás de COLLECTED.
     */
    stage1AdvanceAllocatedRows() {
      const ships = this.collectionStagesVisual?.stage2Shippings || [];
      const out = [];
      ships.forEach((s) => {
        (s.advanceRows || []).forEach((r) => {
          if (!r || !(Number(r.amount) > 0.005)) return;
          out.push({
            _id: String(r._id),
            amount: Number(r.amount),
            collectId: r.collectId,
            collectName: r.collectName || "",
            shippingId: s.shippingId,
            shipName: (s.name && String(s.name).trim()) || "",
          });
        });
      });
      return out;
    },
    /** El texto «arrastra a un embarque» solo aplica si hay chips solo-proforma. */
    showStage1ProformOnlyChips() {
      const lines = this.collectionStagesVisual?.stage1CollectLines || [];
      return lines.some((b) => (b.rows || []).some((r) => Number(r.amount) > 0.005));
    },
    showStage1DragAndDropSection() {
      const csv = this.collectionStagesVisual;
      if (!csv || !csv.stage1) return false;
      const lines = csv.stage1CollectLines || [];
      if (lines.length) return true;
      if (this.stage2HasCollectChipsForDnD) return true;
      const ships = csv.stage2Shippings || [];
      if (Number(csv.stage1.advancePct) > 0 && ships.length > 0) return true;
      return false;
    },
    stage1HeaderDone() {
      const csv = this.collectionStagesVisual;
      if (!csv || !csv.stage1) return false;
      const s = csv.stage1;
      if (!(Number(s.advancePct) > 0.01)) return false;
      return (Number(s.remainingAmount) || 0) <= 0.01;
    },
    /** Zona «volver a solo-proforma» (etapa 1): cobros en embarques, cupo de adelanto o drag activo. */
    showAdvanceReturnToProformDropzone() {
      const csv = this.collectionStagesVisual;
      if (!csv || !csv.stage2Shippings || !csv.stage2Shippings.length) return false;
      if (this.stagesDndActive && this.stagesDndFromStage2) return true;
      if (this.stage2HasCollectChipsForDnD) return true;
      if (Number(csv.stage1 && csv.stage1.advancePct) > 0.01 && !this.stage1HeaderDone) return true;
      return false;
    },
    /**
     * Borde rojo punteado solo en «Drop here… back to proforma-only»,
     * desde que se levanta un drag válido (embarque → proforma), no al posar encima.
     */
    stagesDndReturnDropzoneActive() {
      if (!this.stagesDndActive) return false;
      if (!this.showAdvanceReturnToProformDropzone) return false;
      if (this.stagesDndNotaMode === "shipping_to_proform" && this.fobNcShortcutUiAllowed) {
        return true;
      }
      if (!this.canCollect || !this.stagesDndCollectKind) return false;
      const k = this.stagesDndCollectKind;
      if (k === "ship_advance" || k === "shipping_only") {
        return this.stagesDndFromStage2;
      }
      return false;
    },
    /**
     * Contenedores de la proforma vs. la suma de los de sus embarques, con los mismos
     * números que muestran las tarjetas. Solo es inconsistencia cuando los embarques
     * suman de más: que sumen de menos es la operación a medio embarcar, y eso ya lo
     * dice el cupo pendiente («faltan asignar N contenedores»). `null` cuando no hay
     * con qué comparar (sin embarques, o datos todavía sin cargar).
     * @returns {{proform:number, sum:number, ships:{name:string, qty:number}[]}|null}
     */
    proformContainerMismatch() {
      const ships = this.collectionStagesVisual?.stage2Shippings;
      if (!Array.isArray(ships) || ships.length === 0) return null;
      const rows = [];
      for (const ship of ships) {
        const qty = this.shippingQtyContainers(ship);
        if (!Number.isFinite(qty)) return null; // dato incompleto: no avisar
        rows.push({ name: (ship.name && String(ship.name).trim()) || "—", qty });
      }
      const sum = rows.reduce((acc, r) => acc + r.qty, 0);
      const proform = Number(this.resolveProformContainerCount());
      if (!Number.isFinite(proform) || !Number.isFinite(sum)) return null;
      if (sum - proform < 0.01) return null; // coinciden o todavía falta embarcar
      return { proform, sum, ships: rows };
    },
    /**
     * Adelanto «descubierto» solo por clasificación: falta adelanto y, al mismo tiempo,
     * hay plata del cliente cobrada contra la factura de los embarques y cupo de adelanto
     * libre para recibirla. No falta plata: falta etiqueta, y moverla lo resuelve.
     * @returns {{remaining:number, invoiceOnly:number, room:number, movable:number}|null}
     */
    advanceShortWithMoneyOnShippingInvoices() {
      const csv = this.collectionStagesVisual;
      const s1 = csv && csv.stage1;
      if (!s1) return null;
      const remaining = Number(s1.remainingAmount) || 0;
      if (!(remaining > 0.01)) return null;
      const ships = Array.isArray(csv.stage2Shippings) ? csv.stage2Shippings : [];
      let invoiceOnly = 0;
      let room = 0;
      ships.forEach((sh) => {
        invoiceOnly += (sh.shippingOnlyRows || []).reduce(
          (acc, r) => acc + (Number(r.amount) || 0),
          0
        );
        if (sh.expectedAdvanceOnShipping == null) return;
        const expected = Number(sh.expectedAdvanceOnShipping) || 0;
        const assigned = Number(sh.advanceAssignedAmount) || 0;
        if (expected > 0) room += Math.max(0, expected - assigned);
      });
      if (!(invoiceOnly > 0.01) || !(room > 0.01)) return null;
      const movable = Math.min(remaining, invoiceOnly, room);
      if (!(movable > 0.01)) return null;
      return { remaining, invoiceOnly, room, movable };
    },
    containersAllocatedMatchProform() {
      if (!this.itemData) return true;
      const pq = this.resolveProformContainerCount();
      if (!Number.isFinite(pq) || pq <= 0) return true;
      const rows =
        this.balanceData && Array.isArray(this.balanceData.shippings)
          ? this.balanceData.shippings
          : null;
      if (!rows || rows.length === 0) return false;
      const sum = rows.reduce((acc, sh) => acc + (Number(sh.qtycontainers) || 0), 0);
      return Math.abs(pq - sum) < 0.01;
    },
    shippingsContainersSum() {
      const rows = this.balanceData?.shippings;
      if (!Array.isArray(rows)) return 0;
      return rows.reduce((acc, sh) => acc + (Number(sh.qtycontainers) || 0), 0);
    },
    containerSlotsRemaining() {
      const pq = this.resolveProformContainerCount();
      if (!(pq > 0)) return 0;
      const sum = this.shippingsContainersSum;
      const diff = pq - sum;
      return diff > 0.001 ? diff : 0;
    },
    showMissingShippingPlaceholder() {
      return !!this.collectionStagesVisual && this.containerSlotsRemaining > 0.001;
    },
    /**
     * Etapa 2 sin tarjetas de embarque y sin placeholder de «faltan asignar»:
     * suele ser proforma con total de contenedores 0 (no hay cupo para mostrar Add / Quick shipping).
     */
    stage2EmptyStateProformContainersZero() {
      const csv = this.collectionStagesVisual;
      if (!csv) return false;
      const ships = csv.stage2Shippings;
      if (Array.isArray(ships) && ships.length > 0) return false;
      if (this.showMissingShippingPlaceholder) return false;
      return this.resolveProformContainerCount() <= 0.001;
    },
    missingShippingContainersTotalRounded() {
      return Math.round(this.resolveProformContainerCount());
    },
    missingShippingContainersShippedRounded() {
      return Math.round(this.shippingsContainersSum);
    },
    missingShippingContainersProgressWidth() {
      const total = Number(this.resolveProformContainerCount());
      const shipped = Number(this.shippingsContainersSum) || 0;
      if (!Number.isFinite(total) || total <= 0) return 0;
      return Math.min(100, Math.max(0, Math.round((shipped / total) * 100)));
    },
    missingShippingContainersProgressClass() {
      const w = this.missingShippingContainersProgressWidth;
      if (w >= 100) return "bg-success";
      if (w > 0) return "bg-info";
      return "bg-secondary";
    },
    stage2HeaderDone() {
      const csv = this.collectionStagesVisual;
      if (!csv || !csv.stage2Shippings || !csv.stage2Shippings.length) return false;
      for (let i = 0; i < csv.stage2Shippings.length; i++) {
        if (!this.isShippingCollectComplete(csv.stage2Shippings[i])) return false;
      }
      return this.containersAllocatedMatchProform;
    },
    proformStagesProgressWidth() {
      const s = this.collectionStagesVisual && this.collectionStagesVisual.stage1;
      if (!s) return 0;
      const exp = Number(s.expectedAmount) || 0;
      const col = Number(s.collectedAmount) || 0;
      if (exp <= 0) return 0;
      return Math.min(100, Math.round((col / exp) * 100));
    },
    proformStagesProgressClass() {
      const w = this.proformStagesProgressWidth;
      if (w >= 100) return "bg-success";
      if (w > 0) return "bg-info";
      return "bg-secondary";
    },
    /** Misma resolución que `FinancialSummary.resolvedCollectionClientId` / vista FOB operación. */
    collectionStagesResolvedClientId() {
      const fd = this.financialData;
      if (fd && fd.targetClientId != null && fd.targetClientId !== "") {
        return String(fd.targetClientId);
      }
      const ti = fd && fd.targetItem;
      const fromTarget = this.extractClientId(ti && ti.client);
      if (fromTarget) return fromTarget;
      return this.extractClientId(this.itemData && this.itemData.client);
    },
    collectionStagesResolvedClientName() {
      const fd = this.financialData;
      if (fd && fd.targetClientName != null && String(fd.targetClientName).trim() !== "") {
        return String(fd.targetClientName).trim();
      }
      const ti = fd && fd.targetItem;
      const fromTarget = this.extractClientName(ti && ti.client);
      if (fromTarget) return fromTarget;
      return this.extractClientName(this.itemData && this.itemData.client) || "";
    },
    collectionStagesShowClientUnderStep1() {
      const id = this.collectionStagesResolvedClientId;
      const name = this.collectionStagesResolvedClientName;
      return !!(id || (name && String(name).trim() !== ""));
    },
    collectionStagesResolvedClientDisplayName() {
      const name = this.collectionStagesResolvedClientName;
      if (name && String(name).trim() !== "") return String(name).trim();
      if (this.collectionStagesResolvedClientId) {
        return this.$t("financialSummary.summaryClientFallback");
      }
      return "";
    },
  },
  methods: {
    formatNumber(number) {
      return formatNumberSync(number || 0, null, true);
    },
    /** Obligación proveedor en factura/gasto: neto de NC/ND proveedor (Recibir) según balance API. */
    balanceRowSupplierNetInvoiceAmount(row) {
      if (!row) return 0;
      const effRaw = row.supplierInvoiceEffective;
      if (effRaw != null && effRaw !== "" && Number.isFinite(Number(effRaw))) {
        return Number(effRaw);
      }
      const gross = Number(row.supplierInvoice) || 0;
      const nc = Number(row.supplierNotaCreditApplied) || 0;
      const nd = Number(row.supplierNotaDebitApplied) || 0;
      return gross - nc + nd;
    },
    shippingStage2SupplierDisplayName(ship) {
      if (!ship) return "";
      const fromFlat = ship.shipperName != null ? String(ship.shipperName).trim() : "";
      if (fromFlat) return fromFlat;
      const sp = ship.shipper;
      if (sp && typeof sp === "object" && sp.name != null && String(sp.name).trim() !== "") {
        return String(sp.name).trim();
      }
      return "";
    },
    shippingStage2SupplierContactId(ship) {
      return this.extractClientId(ship && ship.shipper);
    },
    /** Fila de balance API (`/payments/proform/:id/balance`) para el mismo shipping que la tarjeta de etapa 2. */
    balanceShippingRowForStage2(ship) {
      const rows = this.balanceData && Array.isArray(this.balanceData.shippings) ? this.balanceData.shippings : null;
      if (!rows || !ship || ship.shippingId == null) return null;
      const sid = String(ship.shippingId);
      return rows.find((r) => r && String(r._id) === sid) || null;
    },
    /** Suma de montos de shipping_expenses (proveedor + otros) para la barra de pagos etapa 2. */
    shippingPaymentsProgressDenom(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return 0;
      let t = Number(row.totalPayments);
      if (!Number.isFinite(t) || t < 0) {
        t = this.balanceRowSupplierNetInvoiceAmount(row) + (Number(row.costsTotal) || 0);
      }
      if (t <= 0.01 && Array.isArray(row.costs) && row.costs.length) {
        const fromCosts = row.costs.reduce((acc, c) => acc + (Number(c.amount) || 0), 0);
        const sup = this.balanceRowSupplierNetInvoiceAmount(row);
        const alt = sup + fromCosts;
        if (alt > 0.01) return alt;
      }
      return t;
    },
    /** Suma de payment_appliedto.amount (pagado + solicitado) sobre esos gastos. */
    shippingPaymentsProgressApplied(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return 0;
      let a = Number(row.totalPaymentsAll);
      if (!Number.isFinite(a) || a < 0) {
        a =
          (Number(row.supplierPaid) || 0) +
          (Number(row.supplierRequested) || 0) +
          (Number(row.costsPaid) || 0) +
          (Number(row.costsRequested) || 0);
      }
      return a;
    },
    /** Pagos marcados `paid` (liquidados en banco) aplicados a gastos del embarque. */
    shippingPaymentsProgressPaidAmount(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return 0;
      let p = Number(row.totalPaymentsPaid);
      if (!Number.isFinite(p) || p < 0) {
        p = (Number(row.supplierPaid) || 0) + (Number(row.costsPaid) || 0);
      }
      return Math.max(0, p);
    },
    /** Pagos solicitados aún no marcados como pagados (`paid === false`). */
    shippingPaymentsProgressRequestedPendingAmount(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return 0;
      return (
        (Number(row.supplierRequested) || 0) +
        (Number(row.costsRequested) || 0)
      );
    },
    /** Ancho % (0–100) del tramo azul: pagado / total a cubrir. */
    shippingPaymentsProgressPaidWidthPct(ship) {
      const denom = this.shippingPaymentsProgressDenom(ship);
      const paid = this.shippingPaymentsProgressPaidAmount(ship);
      if (!Number.isFinite(denom) || denom <= 0.01) return 0;
      return Math.min(100, Math.max(0, (paid / denom) * 100));
    },
    /** Ancho % del tramo amarillo: solicitado pendiente; no solapa el azul. */
    shippingPaymentsProgressRequestedWidthPct(ship) {
      const denom = this.shippingPaymentsProgressDenom(ship);
      const paidW = this.shippingPaymentsProgressPaidWidthPct(ship);
      if (!Number.isFinite(denom) || denom <= 0.01) return 0;
      const req = this.shippingPaymentsProgressRequestedPendingAmount(ship);
      const raw = (req / denom) * 100;
      return Math.min(Math.max(0, 100 - paidW), Math.max(0, raw));
    },
    shippingPaymentsProgressWidth(ship) {
      const total = this.shippingPaymentsProgressDenom(ship);
      const applied = this.shippingPaymentsProgressApplied(ship);
      if (!Number.isFinite(total) || total <= 0.01) return 0;
      const pct = (applied / total) * 100;
      return Math.min(100, Math.max(0, Math.round(pct)));
    },
    shippingPaymentsProgressClass(ship) {
      const w = this.shippingPaymentsProgressWidth(ship);
      if (w >= 100) return "bg-success";
      if (w > 0) return "bg-info";
      return "bg-secondary";
    },
    /** Factura proveedor cargada en el embarque (`total_invoicesupplier` > 0). */
    shippingHasInvoiceSupplierLoaded(ship) {
      if (!ship) return false;
      const raw = ship.total_invoicesupplier;
      if (raw == null || raw === "") return false;
      return Number(raw) > 0.01;
    },
    /** Total destacado en pagos etapa 2: factura proveedor si ya está cargada; si no, suma de gastos estimados. */
    shippingPaymentsTotalCostsAmount(ship) {
      if (this.shippingHasInvoiceSupplierLoaded(ship)) {
        const row = this.balanceShippingRowForStage2(ship);
        if (!row) return Number(ship.total_invoicesupplier) || 0;
        // Lo dividido con «Divide payment» sigue siendo parte de la factura del proveedor.
        const splitTotal = this.shippingPaymentExpenseLines(ship)
          .filter((line) => line.splitFrom)
          .reduce((s, line) => s + (Number(line.amount) || 0), 0);
        return this.balanceRowSupplierNetInvoiceAmount(row) + splitTotal;
      }
      const lines = this.shippingPaymentExpenseLines(ship);
      const sumLines = lines.reduce((s, line) => s + (Number(line && line.amount) || 0), 0);
      if (sumLines > 0.01) return sumLines;
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return 0;
      const sup = this.balanceRowSupplierNetInvoiceAmount(row);
      const ct = Number(row.costsTotal) || 0;
      const combined = sup + ct;
      if (combined > 0.01) return combined;
      return sumLines;
    },
    /** Pagos vinculados al gasto: pendientes primero, luego ya pagados (para pastillas). */
    sortExpenseLinkedPayments(arr) {
      const list = Array.isArray(arr) ? arr.slice() : [];
      list.sort((a, b) => {
        const ap = a && (a.paid === true || a.paid === "true" || a.paid === 1) ? 1 : 0;
        const bp = b && (b.paid === true || b.paid === "true" || b.paid === 1) ? 1 : 0;
        return ap - bp;
      });
      return list;
    },
    /** Líneas de gastos (factura proveedor + otros) desde la fila de balance del embarque. */
    shippingPaymentExpenseLines(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return [];
      const out = [];
      const supAmt = this.balanceRowSupplierNetInvoiceAmount(row);
      const supId = row.supplierExpenseId != null ? String(row.supplierExpenseId) : null;
      const supTrashId =
        row.supplierExpenseTrashId != null ? String(row.supplierExpenseTrashId) : null;
      const inTrash = !!(row && row.supplierExpenseInTrash);
      const supplierViewId = supId || supTrashId || null;
      if (supAmt > 0.005 || supId || inTrash) {
        const raw =
          Array.isArray(row.supplierLinkedPayments) && row.supplierLinkedPayments.length
            ? row.supplierLinkedPayments
            : Array.isArray(row.supplierPendingPayments)
              ? row.supplierPendingPayments
              : [];
        out.push({
          key: "supplier",
          /** La factura de la mercadería lleva las notas del rol «Supplier». */
          notaRoleKey: "supplier",
          expenseId: supId,
          expenseInTrash: inTrash,
          /** Para abrir `/dynamic-model/shipping_expenses/view/:id` (activo o papelera). */
          viewExpenseId: supplierViewId,
          labelIsKey: true,
          labelKey: "financialSummary.stages.shipPaymentsExpenseSupplierInvoice",
          contactId:
            row.supplierContactId != null && row.supplierContactId !== ""
              ? String(row.supplierContactId)
              : null,
          contactName: row.supplierContactName || "",
          amount: supAmt,
          paid: Number(row.supplierPaid) || 0,
          requested: Number(row.supplierRequested) || 0,
          duplicatedApplied: Number(row.supplierDuplicatedApplied) || 0,
          duplicatedAppliedPayments: Number(row.supplierDuplicatedAppliedPayments) || 0,
          linkedPayments: this.sortExpenseLinkedPayments(raw),
        });
      }
      const costs = Array.isArray(row.costs) ? row.costs : [];
      costs.forEach((c, idx) => {
        if (!c) return;
        // El importe que queda por pagar ya viene neto de las notas de ese mismo gasto.
        const amt = Number.isFinite(Number(c.amountEffective))
          ? Number(c.amountEffective)
          : Number(c.amount) || 0;
        const eid = c._id != null ? String(c._id) : null;
        const contactIdRaw = c.contactId != null ? c.contactId : c.contact;
        const contactId = contactIdRaw != null && contactIdRaw !== "" ? String(contactIdRaw) : null;
        if (amt <= 0.005 && !eid) return;
        const raw =
          Array.isArray(c.linkedPayments) && c.linkedPayments.length
            ? c.linkedPayments
            : Array.isArray(c.pendingPayments)
              ? c.pendingPayments
              : [];
        out.push({
          key: eid ? `cost-${eid}` : `cost-idx-${idx}`,
          expenseId: eid,
          viewExpenseId: eid || null,
          labelIsKey: false,
          labelText: c.name || "—",
          contactId,
          contactName: c.contactName || "",
          /** Rol con el que se le ligan notas comerciales a este gasto. */
          notaRoleKey: String(c.notaRoleKey || ""),
          /** Tramo de «Divide payment»: gasto al proveedor del que salió. */
          splitFrom: c.splitFrom != null && c.splitFrom !== "" ? String(c.splitFrom) : null,
          amountGross: Number(c.amount) || 0,
          amount: amt,
          paid: Number(c.paid) || 0,
          requested: Number(c.requested) || 0,
          duplicatedApplied: Number(c.duplicatedApplied) || 0,
          duplicatedAppliedPayments: Number(c.duplicatedAppliedPayments) || 0,
          linkedPayments: this.sortExpenseLinkedPayments(raw),
        });
      });
      return this.pairSupplierSplitLines(out);
    },
    /**
     * Los tramos de «Divide payment» van pegados a la factura del proveedor, a la par:
     * así se ve que el monto de la factura es lo que queda después de dividir.
     */
    pairSupplierSplitLines(lines) {
      const supplier = lines.find((l) => l.key === "supplier");
      if (!supplier || !supplier.expenseId) return lines;
      const splits = lines.filter((l) => l.splitFrom && l.splitFrom === supplier.expenseId);
      if (!splits.length) return lines;
      const rest = lines.filter((l) => l !== supplier && !splits.includes(l));
      return [
        { ...supplier, pairedWithSplits: true },
        ...splits.map((l) => ({
          ...l,
          pairedWithSplits: true,
          labelIsKey: true,
          labelKey: "financialSummary.stages.shipPaymentsExpenseSupplierInvoiceSplit",
        })),
        ...rest,
      ];
    },
    expensePaymentPillIsPaid(pmt) {
      return !!(pmt && (pmt.paid === true || pmt.paid === "true" || pmt.paid === 1 || pmt.paid === "1"));
    },
    /** Pastilla: icono reloj verde / rojo según fecha prevista vs hoy (calendario local). */
    expensePaymentPillEstimatedClockClass(pmt) {
      if (!pmt || this.expensePaymentPillIsPaid(pmt) || !pmt.estimated_paymentdate) return "";
      const est = toYyyyMmDdSync(pmt.estimated_paymentdate);
      if (!est) return "";
      const today = calendarTodayLocalYyyyMmDd();
      return est === today ? "text-danger" : "text-success";
    },
    openMarkPaymentPaidModal(payment) {
      if (!payment || !payment._id) return;
      this.$emit("payment-action", { type: "markPaymentPaid", payment });
    },
    openQuickMarkPaymentPaidModal(payment) {
      if (!payment || !payment._id) return;
      this.$emit("payment-action", { type: "quickMarkPaymentPaid", payment });
    },
    openQuickEditPaymentModal(payment) {
      if (!payment || !payment._id) return;
      this.$emit("payment-action", { type: "quickEditPayment", payment });
    },
    deleteLinkedPayment(payment) {
      if (!payment || !payment._id) return;
      this.$emit("payment-action", { type: "deletePayment", payment });
    },
    goToPayment(paymentId) {
      if (paymentId == null || paymentId === "") return;
      const id =
        typeof paymentId === "object" && paymentId !== null && paymentId._id != null
          ? String(paymentId._id)
          : String(paymentId);
      if (!id) return;
      window.open(`/dynamic-model/payment/view/${encodeURIComponent(id)}`, "_blank");
    },
    shippingPaymentLineCommitted(line) {
      return (Number(line.paid) || 0) + (Number(line.requested) || 0);
    },
    /**
     * Importe que «Pag./ped.» está contando de más porque un mismo pago quedó aplicado más de
     * una vez al gasto. Las pastillas de abajo agrupan por pago, así que sin este aviso el
     * número de arriba no cierra con lo que se ve y hay que ir a buscarlo a la base.
     */
    shippingPaymentLineDuplicatedApplied(line) {
      return Number(line && line.duplicatedApplied) || 0;
    },
    /** Cuántos pagos distintos están repetidos sobre el gasto. */
    shippingPaymentLineDuplicatedPayments(line) {
      return Number(line && line.duplicatedAppliedPayments) || 0;
    },
    shippingPaymentLineHasDuplicatedApplied(line) {
      return this.shippingPaymentLineDuplicatedApplied(line) > 0.01;
    },
    /** Veces que el pago de la pastilla está aplicado al gasto (1 = normal). */
    expensePaymentPillTimesApplied(pmt) {
      const times = Number(pmt && pmt.timesApplied) || 1;
      return times > 1 ? times : 1;
    },
    shippingPaymentLinePending(line) {
      const amt = Number(line.amount) || 0;
      return Math.max(0, amt - this.shippingPaymentLineCommitted(line));
    },
    /** Cubierto (pendiente 0) pero aún hay solicitud sin marcar pagada en banco. */
    shippingPaymentLineCoveredAwaitingBank(line) {
      if (!line || this.shippingPaymentLinePending(line) > 0.01) return false;
      const linked = Array.isArray(line.linkedPayments) ? line.linkedPayments : [];
      return linked.some((pmt) => pmt && !this.expensePaymentPillIsPaid(pmt));
    },
    /** Gasto de embarque cubierto (pagado + solicitado ≥ monto del gasto), mismo criterio que pendiente $0. */
    shippingPaymentExpenseLineFullyPaid(line) {
      if (!line) return false;
      return this.shippingPaymentLinePending(line) <= 0.01;
    },
    shippingPaymentLineCanRequest(line) {
      if (!line || !line.expenseId) return false;
      return this.shippingPaymentLinePending(line) > 0.01;
    },
    shippingPaymentExpenseLineLabel(line) {
      if (!line) return "—";
      if (line.labelIsKey && line.labelKey) return this.$t(line.labelKey);
      return line.labelText || "—";
    },
    shippingPaymentExpenseLineContactName(line) {
      if (!line) return "";
      if (line.contactName != null && String(line.contactName).trim() !== "") {
        return String(line.contactName).trim();
      }
      const contact = line.contact;
      if (contact && typeof contact === "object" && contact.name != null) {
        const n = String(contact.name).trim();
        if (n) return n;
      }
      return "";
    },
    shippingPaymentExpenseLineContactId(line) {
      if (!line) return null;
      const raw =
        line.contactId != null
          ? line.contactId
          : line.contact && typeof line.contact === "object" && line.contact._id != null
            ? line.contact._id
            : line.contact;
      if (raw == null || raw === "") return null;
      return String(raw);
    },
    goToContact(contactId) {
      if (contactId == null || contactId === "") return;
      const id =
        typeof contactId === "object" && contactId !== null && contactId._id != null
          ? String(contactId._id)
          : String(contactId);
      if (!id) return;
      window.open(`/dynamic-model/Contact/view/${encodeURIComponent(id)}`, "_blank");
    },
    /** Abre el registro dinámico `shipping_expenses` (vista), p. ej. gasto en papelera. */
    openShippingExpenseDynamicView(line) {
      const id = line && line.viewExpenseId;
      if (!id) return;
      window.open(
        `/dynamic-model/shipping_expenses/view/${encodeURIComponent(String(id))}`,
        "_blank"
      );
    },
    goToContactSameTab(contactId) {
      if (contactId == null || contactId === "") return;
      const id =
        typeof contactId === "object" && contactId !== null && contactId._id != null
          ? String(contactId._id)
          : String(contactId);
      if (!id) return;
      window.location.assign(`/dynamic-model/Contact/view/${encodeURIComponent(id)}`);
    },
    openEditShippingExpense(ship, line) {
      if (!line || !line.expenseId) return;
      const sid =
        ship && ship.shippingId != null && ship.shippingId !== ""
          ? ship.shippingId
          : ship && ship._id != null
            ? ship._id
            : null;
      this.$emit("payment-action", {
        type: "editShippingExpense",
        expenseId: String(line.expenseId),
        ...(sid != null ? { shippingId: String(sid) } : {}),
      });
    },
    requestShippingExpensePayment(ship, line) {
      if (!this.shippingPaymentLineCanRequest(line)) return;
      const pending = this.shippingPaymentLinePending(line);
      let amountSuggested = null;
      if (line && line.key === "supplier") {
        const pct = this.supplierAdvancePctDisplay(ship);
        const base = Number(line.amount) || 0;
        if (pct && base > 0.005) {
          const advanceTarget = parseFloat(((base * pct) / 100).toFixed(2));
          if (advanceTarget >= 0.01) {
            const committed = this.shippingPaymentLineCommitted(line);
            const remainingAdvanceSlot = parseFloat(
              Math.max(0, advanceTarget - committed).toFixed(2)
            );
            if (remainingAdvanceSlot >= 0.01) {
              amountSuggested = Math.min(pending, remainingAdvanceSlot);
            }
          }
        }
      }
      this.$emit("payment-action", {
        type: "payExpense",
        expenseId: line.expenseId,
        expenseName: this.shippingPaymentExpenseLineLabel(line),
        amountMax: pending,
        amountSuggested,
      });
    },
    requestShippingExpenseSplitPayment(ship, line) {
      if (!this.shippingPaymentLineCanRequest(line)) return;
      if (!line || line.key !== "supplier") return;
      const sid =
        ship && ship.shippingId != null && ship.shippingId !== ""
          ? ship.shippingId
          : ship && ship._id != null
            ? ship._id
            : null;
      const pending = this.shippingPaymentLinePending(line);
      this.$emit("payment-action", {
        type: "divideSupplierPayment",
        expenseId: line.expenseId,
        expenseName: this.shippingPaymentExpenseLineLabel(line),
        amountMax: pending,
        amountSuggested: pending,
        ...(sid != null ? { shippingId: String(sid) } : {}),
      });
    },
    quickCollectConfirmMoney(value) {
      const n = Number(value);
      const x = Number.isFinite(n) ? n : 0;
      return "$" + x.toFixed(2);
    },
    async ensureListsLoaded() {
      if (this.lists && this.lists.length) return;
      if (this.cachedLists && this.cachedLists.length) return;
      try {
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.cachedLists = Array.isArray(data) ? data : [];
      } catch {
        this.cachedLists = [];
      }
    },
    /**
     * % de adelanto para mostrar. Cargado por monto, el porcentaje queda derivado con
     * decimales (28.407009), así que se recorta a 2 y se le sacan los ceros de sobra.
     * @param {number|string} pct
     * @returns {string}
     */
    formatAdvancePct(pct) {
      const n = Number(pct);
      if (!Number.isFinite(n)) return "0";
      return String(Math.round(n * 1e2) / 1e2);
    },
    /**
     * Aviso al entrar a la operación cuando los contenedores de la proforma no coinciden
     * con la suma de sus embarques. Detalla la corrección antes de hacerla; escribir el
     * dato queda a un clic explícito del usuario.
     */
    async promptProformContainerMismatch(mismatch) {
      if (!mismatch || !this.itemData?._id) return;
      const esc = (value) =>
        String(value == null ? "" : value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const num = (n) => formatNumberSync(Math.round(Number(n) || 0), 0);
      const detail = mismatch.ships
        .map(
          (row) =>
            `<div class="d-flex justify-content-between gap-2"><span>${esc(
              row.name
            )}</span><strong class="tabular-nums">${num(row.qty)}</strong></div>`
        )
        .join("");
      const html = `
<div class="text-start small mb-2">${esc(
        this.$t("financialSummary.stages.containerMismatchBody", {
          proform: num(mismatch.proform),
          sum: num(mismatch.sum),
        })
      )}</div>
<div class="text-start small border rounded p-2 bg-light mb-2">
  <div class="text-muted text-uppercase mb-1" style="font-size:0.62rem;letter-spacing:0.03em">${esc(
    this.$t("financialSummary.stages.containerMismatchDetailCaption")
  )}</div>
  ${detail}
</div>
<div class="text-start small">${esc(
        this.$t("financialSummary.stages.containerMismatchFixNote", { sum: num(mismatch.sum) })
      )}</div>`;
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: this.$tn("financialSummary.stages.containerMismatchTitle"),
        html,
        showCancelButton: true,
        confirmButtonText: this.$tn("financialSummary.stages.containerMismatchFix"),
        cancelButtonText: this.$tn("financialSummary.stages.containerMismatchLater"),
      });
      if (!isConfirmed) return;
      await this.fixProformContainersFromShippings(mismatch.sum);
    },
    /** Pone en la proforma la suma de contenedores de sus embarques (los embarques no se tocan). */
    async fixProformContainersFromShippings(sum) {
      if (!this.itemData?._id || this.fixingProformContainers) return;
      const value = Math.round(Number(sum));
      if (!Number.isFinite(value) || value < 0) return;
      this.fixingProformContainers = true;
      try {
        // Endpoint de campo puntual: no dispara las validaciones del formulario completo.
        await wencesApi.put(
          `/api/gotrading/dynamic/field/${encodeURIComponent(
            this.modelName
          )}/${encodeURIComponent(this.itemData._id)}`,
          { qtycontainers: value, partialUpdate: true }
        );
        if (this.itemData) this.$set(this.itemData, "qtycontainers", value);
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.containerMismatchFixedTitle"),
          text: this.$tn("financialSummary.stages.containerMismatchFixedText", {
            sum: formatNumberSync(value, 0),
          }),
          timer: 2600,
          showConfirmButton: true,
        });
        this.$emit("refresh");
      } catch (error) {
        const msg =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error)) ||
          error.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.containerMismatchFixError"),
        });
      } finally {
        this.fixingProformContainers = false;
      }
    },
    /**
     * Plan para pasar al adelanto la plata que está cobrada contra la factura de los
     * embarques, sin superar lo que falta de adelanto ni el cupo de cada embarque.
     * Primero al adelanto del propio embarque del cobro; lo que no entra, a los que
     * todavía tengan cupo.
     * @returns {{collectId:string, collectName:string, fromAppliedToId:string,
     *   fromShipName:string, toShippingId:string, toShipName:string, amount:number}[]}
     */
    buildInvoiceMoneyToAdvanceMoves() {
      const csv = this.collectionStagesVisual;
      const s1 = csv && csv.stage1;
      if (!s1) return [];
      const ships = Array.isArray(csv.stage2Shippings) ? csv.stage2Shippings : [];
      const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));
      let budget = Number(s1.remainingAmount) || 0;
      const room = {};
      const nameById = {};
      ships.forEach((sh) => {
        const id = String(sh.shippingId);
        nameById[id] = (sh.name && String(sh.name).trim()) || "";
        if (sh.expectedAdvanceOnShipping == null) {
          room[id] = 0;
          return;
        }
        const expected = Number(sh.expectedAdvanceOnShipping) || 0;
        const assigned = Number(sh.advanceAssignedAmount) || 0;
        room[id] = expected > 0 ? Math.max(0, expected - assigned) : 0;
      });
      const sources = [];
      ships.forEach((sh) => {
        (sh.shippingOnlyRows || []).forEach((r) => {
          const amount = Number(r.amount) || 0;
          if (amount <= 0.005) return;
          sources.push({
            collectId: r.collectId,
            collectName: r.collectName || "",
            fromAppliedToId: String(r._id),
            fromShippingId: String(sh.shippingId),
            left: amount,
          });
        });
      });
      const moves = [];
      const take = (src, toId) => {
        if (budget <= 0.005 || src.left <= 0.005 || (room[toId] || 0) <= 0.005) return;
        const amount = round2(Math.min(src.left, room[toId], budget));
        if (amount <= 0.005) return;
        moves.push({
          collectId: src.collectId,
          collectName: src.collectName,
          fromAppliedToId: src.fromAppliedToId,
          fromShipName: nameById[src.fromShippingId] || "",
          toShippingId: toId,
          toShipName: nameById[toId] || "",
          amount,
        });
        src.left = round2(src.left - amount);
        room[toId] = round2(room[toId] - amount);
        budget = round2(budget - amount);
      };
      sources.forEach((src) => take(src, src.fromShippingId));
      sources.forEach((src) => {
        ships.forEach((sh) => {
          const id = String(sh.shippingId);
          if (id !== src.fromShippingId) take(src, id);
        });
      });
      return moves;
    },
    /**
     * Detalla los movimientos antes de hacerlos y, si el usuario confirma, los aplica
     * uno por uno con el mismo endpoint que el arrastre.
     */
    async promptMoveInvoiceMoneyToAdvance() {
      if (!this.canCollect || this.redistributeInProgress) return;
      const moves = this.buildInvoiceMoneyToAdvanceMoves();
      if (!moves.length) return;
      const esc = (value) =>
        String(value == null ? "" : value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const money = (n) => "$" + this.formatNumber(Number(n) || 0);
      const total = moves.reduce((acc, m) => acc + m.amount, 0);
      const detail = moves
        .map(
          (m) =>
            `<div class="d-flex justify-content-between gap-2 mb-1"><span>${esc(
              m.collectName || "—"
            )} · ${esc(m.fromShipName)} → ${esc(m.toShipName)}</span><strong class="tabular-nums">${money(
              m.amount
            )}</strong></div>`
        )
        .join("");
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: this.$tn("financialSummary.stages.moveInvoiceMoneyToAdvanceTitle"),
        html: `
<div class="text-start small mb-2">${esc(
          this.$tn("financialSummary.stages.moveInvoiceMoneyToAdvanceIntro", {
            total: money(total),
          })
        )}</div>
<div class="text-start small border rounded p-2 bg-light">${detail}</div>`,
        showCancelButton: true,
        confirmButtonText: this.$tn("financialSummary.stages.moveInvoiceMoneyToAdvanceConfirm"),
        cancelButtonText: this.$tn("financialSummary.close"),
      });
      if (!isConfirmed) return;
      this.redistributeInProgress = true;
      try {
        let moved = 0;
        for (const m of moves) {
          const data = await this.sendAdvanceRedistribute({
            collectId: m.collectId,
            fromAppliedToId: m.fromAppliedToId,
            amount: m.amount,
            toShippingId: m.toShippingId,
            assignmentMode: "proforma_shipping",
          });
          moved += Number(data && data.moved) || 0;
        }
        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: this.$tn("financialSummary.stages.moveInvoiceMoneyToAdvanceOk", {
            amount: money(moved),
          }),
          timer: 3200,
          showConfirmButton: true,
        });
        this.$emit("refresh");
      } catch (error) {
        const msg =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error)) ||
          this.$t("financialSummary.stages.redistributeError");
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: String(msg) });
      } finally {
        this.redistributeInProgress = false;
      }
    },
    /**
     * Abre el modal de configuración del adelanto de la proforma (porcentaje o monto,
     * más la condición de pago). Sirve con adelanto cargado y sin él.
     */
    async openProformAdvanceModal() {
      if (!this.itemData?._id || this.savingProformAdvance) return;
      await this.ensureListsLoaded();
      this.showProformAdvanceModal = true;
    },
    closeProformAdvanceModal() {
      this.showProformAdvanceModal = false;
    },
    /**
     * Guarda el adelanto de la proforma: porcentaje (ya resuelto) y condición de pago.
     * @param {{pct: number, payable: ?string}} config
     */
    async saveProformAdvanceConfig({ pct, payable }) {
      if (!this.itemData?._id || this.savingProformAdvance) return;
      this.savingProformAdvance = true;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/${this.modelName}/${this.itemData._id}`, {
          advance: pct,
          payable,
          partialUpdate: true,
        });
        this.showProformAdvanceModal = false;
        this.$emit("advance-pct-saved");
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.advancePctSaved"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.$emit("refresh");
      } catch (error) {
        console.error("saveProformAdvanceConfig:", error);
        const msg =
          (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
          error.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.advancePctSaveError"),
        });
      } finally {
        this.savingProformAdvance = false;
      }
    },
    supplierAdvanceDraftKey(ship) {
      return ship && ship.shippingId != null ? String(ship.shippingId) : "";
    },
    supplierAdvancePctDisplay(ship) {
      const n = Number(ship && ship.advance_sp);
      if (Number.isFinite(n) && n > 0) return Math.round(n);
      return null;
    },
    /**
     * Datos para la línea de anticipo proveedor (total factura, esperado); null si no aplica.
     */
    supplierInvoiceAdvanceSummaryParts(ship) {
      const pct = this.supplierAdvancePctDisplay(ship);
      if (!pct) return null;
      const row = this.balanceShippingRowForStage2(ship);
      if (!row) return null;
      // Importe efectivo: el mismo que muestra la fila «Supplier invoice» y el que propone
      // el pedido de pago, ya ajustado por las notas comerciales del proveedor.
      const effective = Number(row.supplierInvoiceEffective);
      const total = Number.isFinite(effective) ? effective : Number(row.supplierInvoice) || 0;
      if (total <= 0.005) return null;
      const expected = parseFloat(((total * pct) / 100).toFixed(2));
      return {
        pct,
        total: "$" + formatNumberSync(total, null, true),
        expected: "$" + formatNumberSync(expected, null, true),
      };
    },
    /** Para v-for: un solo elemento `{ pct, tail }` o vacío (evita repetir el método en plantilla). */
    supplierInvoiceAdvanceSummaryLineRichList(line, ship) {
      if (!line || line.key !== "supplier") return [];
      const p = this.supplierInvoiceAdvanceSummaryParts(ship);
      if (!p) return [];
      return [
        {
          pct: p.pct,
          tail: this.$t("financialSummary.stages.supplierInvoiceAdvanceSummaryLineAfterPct", p),
        },
      ];
    },
    onSupplierAdvanceDraftInput(ship, ev) {
      const k = this.supplierAdvanceDraftKey(ship);
      if (!k) return;
      this.$set(this.inlineSupplierAdvanceDraft, k, (ev && ev.target && ev.target.value) || "");
    },
    supplierAdvanceDraftValue(ship) {
      const k = this.supplierAdvanceDraftKey(ship);
      if (!k) return "";
      const v = this.inlineSupplierAdvanceDraft[k];
      return v != null && v !== "" ? String(v) : "";
    },
    async saveInlineSupplierAdvancePct(ship) {
      const key = this.supplierAdvanceDraftKey(ship);
      if (!key || this.savingSupplierAdvanceShippingId) return;
      const raw = this.supplierAdvanceDraftValue(ship);
      const n = raw === "" || raw == null ? NaN : parseInt(String(raw).trim(), 10);
      if (!Number.isFinite(n) || n < 1 || n > 100) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.supplierAdvancePctInvalidTitle"),
          text: this.$tn("financialSummary.stages.supplierAdvancePctInvalid"),
        });
        return;
      }
      this.savingSupplierAdvanceShippingId = key;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/shipping/${key}`, {
          advance_sp: n,
          partialUpdate: true,
        });
        this.$set(this.inlineSupplierAdvanceDraft, key, "");
        if (ship && typeof ship === "object") {
          this.$set(ship, "advance_sp", n);
        }
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.supplierAdvancePctSaved"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.$emit("refresh");
      } catch (error) {
        console.error("saveInlineSupplierAdvancePct:", error);
        const msg =
          (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
          error.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.supplierAdvancePctSaveError"),
        });
      } finally {
        this.savingSupplierAdvanceShippingId = null;
      }
    },
    executeAction(action) {
      if (!action || !action.type) return;
      if (action.type === "edit") {
        this.$emit("edit-item", { modelName: this.modelName, itemId: this.itemData._id });
        return;
      }
      if (action.type === "addCollect") {
        this.openAddCollectionModal();
      }
    },
    async openAddCollectionModal(options = {}) {
      try {
        const withCalc = !!(options && options.withContainerCalculator);
        this.showCollectContainerCalculator = withCalc;
        const csv = this.collectionStagesVisual;
        const s1 = csv && csv.stage1;
        let amountCap = null;
        if (s1) {
          const rem = Number(s1.remainingAmount);
          const exp = Number(s1.expectedAmount);
          if (Number.isFinite(rem) && rem > 0.01) {
            amountCap = rem;
          } else if (Number.isFinite(exp) && exp > 0.01) {
            amountCap = exp;
          }
        }
        this.quickCollectAllowAmountEdit = true;
        this.quickCollectAmountMax = amountCap;
        this.parentID = this.itemData._id;
        this.parentModel = this.modelName || "Proform";
        this.extraParams = { type: "advance" };
        await this.ensureListsLoaded();
        this.showAddCollectionModal = true;
      } catch (error) {
        console.error("openAddCollectionModal:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToOpenCollectionModal"),
        });
      }
    },
    closeAddCollectionModal() {
      this.showAddCollectionModal = false;
      this.parentID = null;
      this.parentModel = null;
      this.extraParams = {};
      this.quickCollectAllowAmountEdit = false;
      this.quickCollectAmountMax = null;
      this.showCollectContainerCalculator = false;
    },
    async openQuickShippingShipperModal() {
      if (!this.itemData?._id) return;
      try {
        this.parentID = String(this.itemData._id);
        this.parentModel = this.modelName || "Proform";
        await this.ensureListsLoaded();
        this.showQuickShippingShipperModal = true;
      } catch (error) {
        console.error("openQuickShippingShipperModal:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToOpenAddShippingModal"),
        });
      }
    },
    closeQuickShippingShipperModal() {
      this.showQuickShippingShipperModal = false;
      this.parentID = null;
      this.parentModel = null;
    },
    async handleQuickShippingShipperSaved() {
      try {
        this.closeQuickShippingShipperModal();
        this.$emit("refresh");
        this.$emit("shipping-saved");
      } catch (error) {
        console.error("handleQuickShippingShipperSaved:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToSaveShipping"),
        });
      }
    },
    async openStage2ShippingCollect(ship, intent) {
      if (!this.canCollect || !ship?.shippingId) return;
      if (intent === "invoice") {
        const inv = this.shipInvoiceTotalDisplay(ship);
        if (!Number.isFinite(inv) || inv <= 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingTitle"),
            text: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingText"),
          });
          return;
        }
      }
      if (this.isShippingCollectComplete(ship)) {
        await Swal.fire({
          icon: "info",
          title: this.$tn("financialSummary.stages.stage2ShippingCollectCompleteTitle"),
          text: this.$tn("financialSummary.stages.stage2ShippingCollectCompleteText"),
        });
        return;
      }
      if (intent === "advance") {
        const s1 = this.collectionStagesVisual?.stage1;
        if (s1 && s1.advancePct > 0 && s1.remainingAmount <= 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.stage2AdvanceCollectBlockedTitle"),
            text: this.$tn("financialSummary.stages.stage2AdvanceCollectBlocked"),
          });
          return;
        }
        const expectedOnShip = Number(ship.expectedAdvanceOnShipping);
        const assignedOnShip = Number(ship.advanceAssignedAmount);
        if (Number.isFinite(expectedOnShip) && expectedOnShip > 0.01) {
          const remainingAdvanceOnShip = expectedOnShip - assignedOnShip;
          if (remainingAdvanceOnShip <= 0.01) {
            await Swal.fire({
              icon: "info",
              title: this.$tn("financialSummary.stages.stage2ShippingAdvanceAlreadyCollectedTitle"),
              text: this.$tn("financialSummary.stages.stage2ShippingAdvanceAlreadyCollectedText"),
            });
            return;
          }
        }
      }
      try {
        this.showCollectContainerCalculator = false;
        this.parentID = String(ship.shippingId);
        this.parentModel = this.shippingCollectParentModel || "shipping";
        this.extraParams = { type: "advance", stage2Intent: intent };

        const s1 = this.collectionStagesVisual?.stage1;
        const poolRem = s1 ? Number(s1.remainingAmount) : NaN;
        let amountCap = null;
        if (intent === "advance") {
          const expectedOnShip = Number(ship.expectedAdvanceOnShipping);
          const assignedOnShip = Number(ship.advanceAssignedAmount);
          let remOnShip = null;
          if (Number.isFinite(expectedOnShip) && expectedOnShip > 0.01) {
            const slice = expectedOnShip - assignedOnShip;
            if (Number.isFinite(slice) && slice > 0.01) {
              remOnShip = slice;
            }
          }
          if (remOnShip != null && Number.isFinite(poolRem) && poolRem > 0.01) {
            amountCap = Math.min(remOnShip, poolRem);
          } else if (remOnShip != null) {
            amountCap = remOnShip;
          } else if (Number.isFinite(poolRem) && poolRem > 0.01) {
            amountCap = poolRem;
          }
        } else if (intent === "invoice") {
          const pending = Number(ship.pendingToCollect);
          if (Number.isFinite(pending) && pending > 0.01) {
            amountCap = pending;
          }
        }
        this.quickCollectAllowAmountEdit = true;
        this.quickCollectAmountMax = amountCap;

        await this.ensureListsLoaded();
        this.showAddCollectionModal = true;
      } catch (error) {
        console.error("openStage2ShippingCollect:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToOpenCollectionModal"),
        });
      }
    },
    async handleCollectionSaved(collectionData) {
      try {
        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: this.$tn("financialSummary.collectionCreatedSuccessfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        this.closeAddCollectionModal();
        this.$emit("refresh");
        this.$emit("collection-saved", collectionData);
      } catch (error) {
        console.error("handleCollectionSaved:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToSaveCollection"),
        });
      }
    },
    async openAddShippingFromProform() {
      if (!this.itemData?._id) return;
      try {
        await this.ensureListsLoaded();
        const item = { custom_proform: this.itemData._id };
        try {
          const { data: referenceMapping } = await wencesApi.get("/api/gotrading/model-reference-mapping", {
            params: {
              sourceModel: this.modelName,
              targetModel: "shipping",
              sourceRecordId: this.itemData._id,
            },
          });
          if (referenceMapping?.data?.length > 0) {
            referenceMapping.data[0].mappings.forEach((mapping) => {
              if (this.itemData[mapping.sourceField] == null) return;
              let value = this.itemData[mapping.sourceField];
              if (Array.isArray(value)) {
                value = value.map((sub) => {
                  const cleanItem = { ...sub };
                  if (cleanItem.products && Array.isArray(cleanItem.products)) {
                    cleanItem.products = cleanItem.products.map((product) => {
                      const cleanProduct = { ...product };
                      delete cleanProduct._id;
                      delete cleanProduct.editHistory;
                      delete cleanProduct.createdAt;
                      delete cleanProduct.updatedAt;
                      delete cleanProduct.__v;
                      return cleanProduct;
                    });
                  }
                  delete cleanItem._id;
                  delete cleanItem.editHistory;
                  delete cleanItem.createdAt;
                  delete cleanItem.updatedAt;
                  delete cleanItem.__v;
                  return cleanItem;
                });
              }
              item[mapping.targetField] = value;
            });
          }
        } catch (mapErr) {
          console.warn("[CollectionSummary] model-reference-mapping shipping:", mapErr);
        }
        const totalContainers = Math.round(this.resolveProformContainerCount());
        if (totalContainers > 0) {
          const rows =
            this.balanceData && Array.isArray(this.balanceData.shippings) ? this.balanceData.shippings : [];
          const picked = await showShippingContainerPickDialog(
            (k, p) => this.$t(k, p),
            totalContainers,
            rows
          );
          if (picked === null) return;
          if (picked.length > 0) {
            patchShippingItemQtyToMatchContainerPick(item, picked.length);
            item.containers = picked.join(",");
          }
        }
        this.addShippingFromProformEditingItem = item;
        this.showAddShippingFromProformModal = true;
      } catch (e) {
        console.error("openAddShippingFromProform:", e);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToOpenAddShippingModal"),
        });
      }
    },
    closeAddShippingFromProformModal() {
      this.showAddShippingFromProformModal = false;
      this.addShippingFromProformEditingItem = null;
    },
    async handleAddShippingFromProformSaved() {
      this.closeAddShippingFromProformModal();
      await Swal.fire({
        icon: "success",
        title: this.$tn("success"),
        text: this.$tn("financialSummary.shippingCreatedFromProformOk"),
        timer: 2200,
        showConfirmButton: false,
      });
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    async viewCollectionDetails() {
      try {
        this.showCollectionDetailsModal = true;
        this.loadingCollectionDetails = true;
        this.collectionDetails = null;
        const targetId = this.itemData._id;
        if (!targetId) throw new Error("No valid ID");
        const response = await wencesApi.get(`/api/financial/collects/${targetId}/details`);
        if (response.data.success) {
          this.collectionDetails = response.data;
        } else {
          throw new Error(response.data.message || "Failed");
        }
      } catch (error) {
        console.error("viewCollectionDetails:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.errorLoadingCollectionDetails"),
        });
        this.showCollectionDetailsModal = false;
      } finally {
        this.loadingCollectionDetails = false;
      }
    },
    closeCollectionDetailsModal() {
      this.showCollectionDetailsModal = false;
      this.collectionDetails = null;
    },
    goToCollect(collectId) {
      if (collectId == null || collectId === "") return;
      const id =
        typeof collectId === "object" && collectId !== null && collectId._id != null
          ? String(collectId._id)
          : String(collectId);
      if (!id) return;
      window.open(`/dynamic-model/collect/view/${encodeURIComponent(id)}`, "_blank");
    },
    openQuickEditCollectRow(payload) {
      if (!this.canCollect || !payload || !payload.collectId) return;
      const ref = this.$refs.quickEditCollectModal;
      if (ref && typeof ref.open === "function") ref.open(payload);
    },
    goToShipping(shippingId) {
      if (shippingId == null || shippingId === "") return;
      const id =
        typeof shippingId === "object" && shippingId !== null && shippingId._id != null
          ? String(shippingId._id)
          : String(shippingId);
      if (!id) return;
      window.open(`/dynamic-model/shipping/view/${encodeURIComponent(id)}`, "_blank");
    },
    resolveProformContainerCount() {
      return resolveProformContainerCountFromRecord(this.itemData, this.balanceData && this.balanceData.proform);
    },
    shippingQtyContainers(ship) {
      if (!ship) return NaN;
      const q = Number(ship.qtycontainers);
      if (Number.isFinite(q)) return q;
      const sid = ship.shippingId != null ? String(ship.shippingId) : "";
      const rows = this.balanceData?.shippings;
      if (!sid || !Array.isArray(rows)) return NaN;
      const row = rows.find((r) => String(r._id) === sid);
      if (!row) return NaN;
      const qc = Number(row.qtycontainers);
      return Number.isFinite(qc) ? qc : 0;
    },
    shippingContainerCountDisplay(ship) {
      const n = this.shippingQtyContainers(ship);
      if (!Number.isFinite(n)) return "—";
      return Math.round(n);
    },
    /**
     * Números de contenedor cargados en la lista de empaque del embarque
     * (`packingListContainers`, sin repetir y en orden de línea).
     * @returns {string[]}
     */
    shippingContainerNumbers(ship) {
      const raw = ship && Array.isArray(ship.packingListContainers) ? ship.packingListContainers : [];
      const seen = new Set();
      const out = [];
      raw.forEach((value) => {
        const text = String(value == null ? "" : value).trim();
        if (!text) return;
        const key = text.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(text);
      });
      return out;
    },
    /** Los contenedores del embarque separados por coma, o "" si no hay ninguno cargado. */
    shippingContainerNumbersText(ship) {
      return this.shippingContainerNumbers(ship).join(", ");
    },
    /**
     * Productos de la factura del embarque (grupo productandprice, solapa Invoice).
     * Se listan debajo de la cantidad de contenedores de cada embarque en la etapa 2.
     */
    shippingInvoiceProductNames(ship) {
      const names = ship && Array.isArray(ship.invoiceProductNames) ? ship.invoiceProductNames : [];
      return names.map((n) => (n == null ? "" : String(n).trim())).filter(Boolean);
    },
    /** Puerto de carga del embarque (viene resuelto del back, con fallback a la proforma). */
    shippingPortOfLoading(ship) {
      if (!ship || ship.portOfLoadingName == null) return "";
      return String(ship.portOfLoadingName).trim();
    },
    /** Puerto de destino del embarque (viene resuelto del back, con fallback a la proforma). */
    shippingPortOfDelivery(ship) {
      if (!ship || ship.portOfDeliveryName == null) return "";
      return String(ship.portOfDeliveryName).trim();
    },
    /** Hay al menos un puerto que mostrar al lado de los productos. */
    shippingHasPorts(ship) {
      if (this.shippingPortOfLoading(ship)) return true;
      return !!this.shippingPortOfDelivery(ship);
    },
    /** Título del bloque de puertos: nombre completo con el país, que en la tarjeta no entra. */
    shippingPortsHint(ship) {
      const line = (label, name, country) => {
        if (!name) return "";
        const full = country ? country + " >> " + name : name;
        return label + ": " + full;
      };
      const country = (key) => {
        if (!ship || ship[key] == null) return "";
        return String(ship[key]).trim();
      };
      const parts = [
        line(
          this.$t("financialSummary.stages.shipPortOfLoading"),
          this.shippingPortOfLoading(ship),
          country("portOfLoadingCountry")
        ),
        line(
          this.$t("financialSummary.stages.shipPortOfDischarge"),
          this.shippingPortOfDelivery(ship),
          country("portOfDeliveryCountry")
        ),
      ].filter(Boolean);
      return parts.join(" — ");
    },
    shipInvoiceTotalDisplay(ship) {
      if (!ship) return 0;
      const t = Number(ship.totalProform);
      return Number.isFinite(t) ? t : 0;
    },
    /** Botón autoload packing list: solo si hay total factura (>0) y sin líneas en packing list; si falta total se muestra «Set invoice quantities» y no este botón. */
    showAutoloadPackingListButton(ship) {
      if (!ship || !this.canEditInvoiceUnitQty) return false;
      if (this.shipInvoiceTotalDisplay(ship) <= 0.01) return false;
      const raw = ship.packingListLineCount;
      if (raw === undefined || raw === null) return true;
      const n = Number(raw);
      if (!Number.isFinite(n)) return true;
      return n <= 0;
    },
    shippingTimelineStepDone(ship, step) {
      if (!ship) return false;
      if (step === "proformSupplier") {
        return (
          ship.shipperInvoiceQuantitiesComplete === true && ship.shipperUnitPricesComplete === true
        );
      }
      if (step === "invoice") {
        return this.shipInvoiceTotalDisplay(ship) > 0.01;
      }
      if (step === "packingList") {
        return Number(ship.packingListLineCount) > 0;
      }
      if (step === "invoiceSupplier") {
        return (
          (Number(ship.total_invoicesupplier) || 0) > 0.01 ||
          (Number(ship.total_proformship) || 0) > 0.01
        );
      }
      return false;
    },
    shippingTimelineStepClickable(ship, step) {
      if (!ship || ship.shippingId == null) return false;
      if (step === "proformSupplier") return !!this.canEditShipperPrices;
      if (step === "invoice") return !!this.canEditInvoiceUnitQty;
      if (step === "packingList") return !!this.canEditInvoiceUnitQty;
      return false;
    },
    openShippingTimelineStep(ship, step) {
      if (!this.shippingTimelineStepClickable(ship, step)) return;
      if (step === "proformSupplier") {
        this.openShipperInvoiceUnitQtyModal(ship);
        return;
      }
      if (step === "invoice") {
        this.openInvoiceUnitQtyModal(ship);
        return;
      }
      if (step === "packingList") {
        this.openAutoloadPackingListModal(ship);
      }
    },
    /** Pie «solo factura embarque»: cobros con appliedto sin proforma (NC/ND van en su propio bloque). */
    shipInvoiceOnlyCollectedDisplay(ship) {
      if (!ship) return 0;
      return Math.max(0, Number(ship.shippingOnlyCollectedAmount) || 0);
    },
    /**
     * Meta de cobro de la factura del embarque: total menos NC más ND (mismo cálculo que
     * `invoiceTarget` en el backend). La NC no es plata cobrada: baja lo que hay que cobrar,
     * así que es el denominador de la barra y el «Due» del saldo, no el total bruto.
     */
    shipInvoiceNetTotalDisplay(ship) {
      if (!ship) return 0;
      const total = this.shipInvoiceTotalDisplay(ship);
      const cr = this.shipClientNotaInvoiceCreditApplied(ship);
      const db = this.shipClientNotaInvoiceDebitApplied(ship);
      const net = total - cr + db;
      return Math.max(0, Number.isFinite(net) ? net : 0);
    },
    /** Cobro factura embarque: monto imputado (recibido + líneas que ya cuentan contra pendiente) vs meta neta — alinea con la parte factura de `shippingStagesProgressWidth`. */
    shippingCollectionsInvoiceCaptionNumerator(ship) {
      if (!ship) return 0;
      const total = this.shipInvoiceNetTotalDisplay(ship);
      const pendRaw = Number(ship.pendingToCollect);
      const pend = Number.isFinite(pendRaw) ? pendRaw : 0;
      const raw = total - pend;
      return Math.max(0, Number.isFinite(raw) ? raw : 0);
    },
    shippingCollectionsInvoiceCaptionDenom(ship) {
      return this.shipInvoiceNetTotalDisplay(ship);
    },
    /**
     * Efecto neto de las NC/ND del cliente sobre lo que queda por cobrar de la factura.
     * Negativo si lo bajan (NC), positivo si lo suben (ND).
     */
    shipClientNotaInvoiceNetEffect(ship) {
      return (
        this.shipClientNotaInvoiceDebitApplied(ship) -
        this.shipClientNotaInvoiceCreditApplied(ship)
      );
    },
    /** NC en factura (embarque), importe aplicado a este shipping. */
    shipClientNotaInvoiceCreditApplied(ship) {
      if (!ship) return 0;
      const v = Number(ship.clientNotaInvoiceCreditApplied);
      return Number.isFinite(v) ? v : 0;
    },
    /** ND en factura (embarque). */
    shipClientNotaInvoiceDebitApplied(ship) {
      if (!ship) return 0;
      const v = Number(ship.clientNotaInvoiceDebitApplied);
      return Number.isFinite(v) ? v : 0;
    },
    shipInvoiceNotaImpactShow(ship) {
      return (
        this.shipClientNotaInvoiceCreditApplied(ship) > 0.01 ||
        this.shipClientNotaInvoiceDebitApplied(ship) > 0.01
      );
    },
    /** Barra de cobro factura embarque: mismo cálculo que el caption `Paid & requested / total` (0–100). */
    shippingStagesProgressWidth(ship) {
      if (!ship) return 0;
      const denom = this.shippingCollectionsInvoiceCaptionDenom(ship);
      if (!Number.isFinite(denom) || denom <= 0.01) return 0;
      const numer = this.shippingCollectionsInvoiceCaptionNumerator(ship);
      const pct = (numer / denom) * 100;
      return Math.round(Math.min(100, Math.max(0, pct)));
    },
    shippingStagesProgressClass(ship) {
      const w = this.shippingStagesProgressWidth(ship);
      if (w >= 100) return "bg-success";
      if (w > 0) return "bg-info";
      return "bg-secondary";
    },
    /**
     * Tramo «adelanto de proforma imputado a este embarque»: meta vs asignado en cobros (sin mirar factura total).
     */
    isShippingAdvanceShareComplete(ship) {
      if (!ship) return false;
      const csv = this.collectionStagesVisual;
      const advPct =
        csv && csv.stage1 && csv.stage1.advancePct != null ? Number(csv.stage1.advancePct) : 0;
      if (!Number.isFinite(advPct) || advPct <= 0.01) return true;
      const expRaw = ship.expectedAdvanceOnShipping;
      if (expRaw == null || expRaw === "") return true;
      const exp = Number(expRaw);
      if (!Number.isFinite(exp) || exp <= 0.01) return true;
      const assigned = Number(ship.advanceAssignedAmount);
      const a = Number.isFinite(assigned) ? assigned : 0;
      return a + 0.02 >= exp;
    },
    isShippingCollectComplete(ship) {
      if (!ship) return false;
      const invTotal = this.shipInvoiceTotalDisplay(ship);
      if (!Number.isFinite(invTotal) || invTotal <= 0.01) return false;
      const pend = Number(ship.pendingToCollect);
      if (!Number.isFinite(pend) || pend > 0.01) return false;
      return this.isShippingAdvanceShareComplete(ship);
    },
    /** Proveedor + otros gastos: cierre solo por pagos efectivamente pagados (paid=true), no por solicitados. */
    isShippingPaymentsBlockComplete(ship) {
      const lines = this.shippingPaymentExpenseLines(ship);
      if (!lines.length) return false;
      return lines.every((line) => {
        const amt = Number(line && line.amount) || 0;
        const paid = Number(line && line.paid) || 0;
        return paid + 0.01 >= amt;
      });
    },
    /** Cobro embarque OK y pagos OK: vista compacta (título, contenedores, margen). */
    isShippingStage2CompactSummary(ship) {
      return !!(ship && this.isShippingCollectComplete(ship) && this.isShippingPaymentsBlockComplete(ship));
    },
    /**
     * Margen actual del embarque (cobrado − pagado). Sale del cálculo único de márgenes
     * del back (calculateMargins): fila `balanceData.shippings[].margins` del API de balance.
     */
    shippingOperationMarginCollectedMinusPaid(ship) {
      const row = this.balanceShippingRowForStage2(ship);
      const current = row && row.margins ? Number(row.margins.current) : NaN;
      return Number.isFinite(current) ? current : null;
    },
    isShippingStage2CompactExpanded(ship) {
      if (!ship || ship.shippingId == null) return false;
      return !!this.shippingStage2CompactExpanded[String(ship.shippingId)];
    },
    toggleShippingStage2CompactExpanded(ship) {
      if (!ship || ship.shippingId == null) return;
      const k = String(ship.shippingId);
      this.$set(this.shippingStage2CompactExpanded, k, !this.shippingStage2CompactExpanded[k]);
    },
    /** Importe cobrado de más en factura embarque (pendingToCollect iría negativo). */
    shipInvoiceOverCollectedAmount(ship) {
      if (!ship) return 0;
      const fromApi = Number(ship.overCollectedOnInvoice);
      if (Number.isFinite(fromApi) && fromApi > 0.01) return fromApi;
      const fromField = Number(ship.overCollectedAmount);
      if (Number.isFinite(fromField) && fromField > 0.01) return fromField;
      const pend = Number(ship.pendingToCollect);
      if (!Number.isFinite(pend)) return 0;
      return pend < -0.01 ? Math.abs(pend) : 0;
    },
    /** Adelanto etapa 1 cobrado de más. */
    stage1OverCollectedAmount() {
      const s1 = this.collectionStagesVisual && this.collectionStagesVisual.stage1;
      if (!s1) return 0;
      const fromField = Number(s1.overCollectedAmount);
      if (Number.isFinite(fromField) && fromField > 0.01) return fromField;
      const rem = Number(s1.remainingAmount);
      const exp = Number(s1.expectedAmount);
      const col = Number(s1.collectedAmount);
      if (Number.isFinite(exp) && Number.isFinite(col) && col > exp + 0.01) {
        return col - exp;
      }
      return 0;
    },
    /** Solo tramo factura embarque: sin pendiente de cobro en factura. */
    isShippingInvoicePendingComplete(ship) {
      if (!ship) return false;
      if (this.shipInvoiceOverCollectedAmount(ship) > 0.01) return false;
      const inv = this.shipInvoiceTotalDisplay(ship);
      if (!Number.isFinite(inv) || inv <= 0.01) return false;
      const p = Number(ship.pendingToCollect);
      return Number.isFinite(p) && p <= 0.01;
    },
    openReceiptModal(collectId) {
      window.open(`/templates/receipt/${collectId}`, "_blank");
    },
    /**
     * Fecha de un campo fecha del documento (cobro, pago): se muestra el día civil
     * guardado, sin reinterpretarlo en la zona del navegador. `new Date(...)` leía
     * "2026-09-08" como medianoche UTC y en UTC-3 lo mostraba como el 7.
     */
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return formatDateSync(dateString, "N/A");
    },
    onAdvanceDragStart(ev, payload) {
      if (!this.canCollect || this.redistributeInProgress) {
        ev.preventDefault();
        return;
      }
      try {
        ev.dataTransfer.setData("application/x-gotrading-advance", JSON.stringify(payload));
        ev.dataTransfer.setData("text/plain", " ");
        ev.dataTransfer.effectAllowed = "move";
        this.stagesDndActive = true;
        this.stagesDndCollectKind = (payload && payload.kind) || null;
        this.stagesDndNotaMode = null;
        const k = this.stagesDndCollectKind;
        this.stagesDndFromStage2 = k === "ship_advance" || k === "shipping_only";
      } catch (e) {
        ev.preventDefault();
      }
    },
    onAdvanceDragEnd() {
      this.clearStagesDndHighlight();
    },
    clearStagesDndHighlight() {
      this.stagesDndActive = false;
      this.stagesDndCollectKind = null;
      this.stagesDndNotaMode = null;
      this.stagesDndFromStage2 = false;
      this.stagesDndReturnHover = false;
    },
    stagesDndHighlightShipAdvance(ship) {
      if (!this.stagesDndActive || !ship) return false;
      if (this.redistributeInProgress) return false;
      if (this.stagesDndNotaMode === "proform_to_ship" && this.fobNcShortcutUiAllowed) {
        return true;
      }
      if (!this.canCollect || !this.stagesDndCollectKind) return false;
      const k = this.stagesDndCollectKind;
      return k === "proform_only" || k === "ship_advance" || k === "shipping_only";
    },
    stagesDndHighlightShipInvoice(ship) {
      if (!this.stagesDndActive || !ship) return false;
      if (this.redistributeInProgress) return false;
      const inv = this.shipInvoiceTotalDisplay(ship);
      const invOk = Number.isFinite(inv) && inv > 0.01;
      if (this.stagesDndNotaMode === "proform_to_ship" && this.fobNcShortcutUiAllowed) {
        return invOk;
      }
      if (!this.canCollect || !this.stagesDndCollectKind) return false;
      if (!invOk) return false;
      const k = this.stagesDndCollectKind;
      return k === "proform_only" || k === "ship_advance" || k === "shipping_only";
    },
    /** Mismo flujo que soltar la pastilla en la zona «adelanto» de un embarque (etapa 2). */
    async assignStage1CollectLineToShippingAdvance(bundle, line, ship) {
      if (!this.canCollect || this.redistributeInProgress) return;
      if (!bundle || !line || !ship || ship.shippingId == null) return;
      const amt = Number(line.amount);
      if (!Number.isFinite(amt) || amt <= 0.005) return;
      const parsed = {
        kind: "proform_only",
        collectId: bundle.collectId,
        fromAppliedToId: line._id,
        maxAmount: amt,
        unapplied: bundle.collectUnapplied,
      };
      await this.promptAdvanceRedistribute(parsed, String(ship.shippingId), "advance");
    },
    /**
     * Botón «→ proforma» en las filas de cobro de la etapa 2: mismo flujo que soltar
     * la pastilla en la zona «volver a solo-proforma». Si la línea ya tiene proforma
     * (es adelanto), no hay nada que mover: se avisa y se corta.
     */
    async sendShippingCollectRowToProform(row, ship, kind) {
      if (!this.canCollect || this.redistributeInProgress) return;
      if (!row || row._id == null) return;
      if (kind === "ship_advance") {
        await Swal.fire({
          icon: "info",
          title: this.$tn("financialSummary.stages.sendRowToProformAlreadyTitle"),
          text: this.$tn("financialSummary.stages.sendRowToProformAlreadyText"),
        });
        return;
      }
      const amt = Number(row.amount);
      if (!Number.isFinite(amt) || amt <= 0.005) return;
      await this.promptAdvanceRedistribute(
        {
          kind: "shipping_only",
          collectId: row.collectId,
          fromAppliedToId: String(row._id),
          maxAmount: amt,
          fromShippingId: ship && ship.shippingId != null ? String(ship.shippingId) : null,
        },
        null
      );
    },
    onAdvanceDragOverDropzone(ev) {
      try {
        const types = Array.from(ev.dataTransfer?.types || []);
        const notaToProformDrag = types.includes("application/x-gotrading-nota-shipping");
        const notaDrag = types.includes("application/x-gotrading-nota-proform");
        const collectDrag = types.includes("application/x-gotrading-advance");
        if (notaToProformDrag || notaDrag) {
          if (!this.fobNcShortcutUiAllowed) return;
          ev.dataTransfer.dropEffect = "move";
          return;
        }
        if (collectDrag) {
          if (!this.canCollect) return;
          ev.dataTransfer.dropEffect = "move";
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** Activa borde rojo solo en el bloque dropReturnProform si el drag es válido para soltar ahí. */
    setStagesDndReturnHoverFromDragEvent(ev) {
      this.stagesDndReturnHover = false;
      if (!this.stagesDndActive || !this.showAdvanceReturnToProformDropzone) return;
      const types = Array.from(ev.dataTransfer?.types || []);
      if (types.includes("application/x-gotrading-nota-shipping")) {
        if (this.fobNcShortcutUiAllowed) this.stagesDndReturnHover = true;
        return;
      }
      if (!types.includes("application/x-gotrading-advance") || !this.canCollect) return;
      const k = this.stagesDndCollectKind;
      if (k === "ship_advance" || k === "shipping_only") {
        this.stagesDndReturnHover = true;
      }
    },
    onAdvanceDragEnterReturnDropzone(ev) {
      this.setStagesDndReturnHoverFromDragEvent(ev);
    },
    onAdvanceDragOverReturnDropzone(ev) {
      this.onAdvanceDragOverDropzone(ev);
      this.setStagesDndReturnHoverFromDragEvent(ev);
    },
    onAdvanceDragLeaveReturnDropzone(ev) {
      const rel = ev.relatedTarget;
      if (rel && ev.currentTarget && ev.currentTarget.contains(rel)) return;
      this.stagesDndReturnHover = false;
    },
    parseAdvanceDnD(ev) {
      try {
        const raw = ev.dataTransfer.getData("application/x-gotrading-advance");
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    },
    /** Cobro asignado a embarque (etapa 2) que se devuelve al bolsillo solo-proforma — reasigna, no aumenta el adelanto cobrado. */
    isRedistributeReturnFromShippingKind(kind) {
      return kind === "ship_advance" || kind === "shipping_only";
    },
    /**
     * @param {'advance'|'invoice'} dropZone - advance = bloque meta adelanto; invoice = bloque factura embarque / solo factura
     */
    async onAdvanceDropOnShipping(ev, ship, dropZone = "advance") {
      if (this.redistributeInProgress) return;
      let notaParsed = null;
      try {
        const rawNota = ev.dataTransfer.getData("application/x-gotrading-nota-proform");
        if (rawNota) notaParsed = JSON.parse(rawNota);
      } catch (e) {
        notaParsed = null;
      }
      if (
        notaParsed &&
        notaParsed.kind === "nota_proform" &&
        typeof this.promptNotaProformLinesToShipping === "function"
      ) {
        if (!this.fobNcShortcutUiAllowed) return;
        if (dropZone === "invoice" && ship) {
          const inv = this.shipInvoiceTotalDisplay(ship);
          if (!Number.isFinite(inv) || inv <= 0.01) {
            await Swal.fire({
              icon: "warning",
              title: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingTitle"),
              text: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingText"),
            });
            return;
          }
        }
        ev.preventDefault();
        await this.promptNotaProformLinesToShipping(notaParsed, ship, dropZone);
        return;
      }
      if (!this.canCollect) return;
      if (dropZone === "invoice" && ship) {
        const inv = this.shipInvoiceTotalDisplay(ship);
        if (!Number.isFinite(inv) || inv <= 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingTitle"),
            text: this.$tn("financialSummary.stages.stage2InvoiceTotalMissingText"),
          });
          return;
        }
      }
      const parsed = this.parseAdvanceDnD(ev);
      if (!parsed) return;
      const zone = dropZone === "invoice" ? "invoice" : "advance";
      const targetId = String(ship.shippingId);
      if (parsed.kind === "proform_only") {
        ev.preventDefault();
        await this.promptAdvanceRedistribute(parsed, targetId, zone);
        return;
      }
      const fromSid =
        parsed.fromShippingId != null && parsed.fromShippingId !== ""
          ? String(parsed.fromShippingId)
          : "";
      /* Cobro directo (solo embarque) → soltar en adelanto del mismo embarque: reparte a línea proforma+embarque. */
      if (
        parsed.kind === "shipping_only" &&
        fromSid &&
        fromSid === targetId &&
        zone === "advance"
      ) {
        ev.preventDefault();
        await this.promptAdvanceRedistribute(parsed, targetId, "advance");
        return;
      }
      if (
        (parsed.kind === "ship_advance" || parsed.kind === "shipping_only") &&
        fromSid &&
        fromSid !== targetId
      ) {
        ev.preventDefault();
        await this.promptAdvanceRedistribute(parsed, targetId, zone);
      }
    },
    async onAdvanceDropReturnToProform(ev) {
      if (this.redistributeInProgress) return;
      this.stagesDndReturnHover = false;
      let notaShipParsed = null;
      try {
        const rawNs = ev.dataTransfer.getData("application/x-gotrading-nota-shipping");
        if (rawNs) notaShipParsed = JSON.parse(rawNs);
      } catch (e) {
        notaShipParsed = null;
      }
      if (
        notaShipParsed &&
        notaShipParsed.kind === "nota_shipping" &&
        typeof this.promptNotaShippingLinesToProform === "function"
      ) {
        if (!this.fobNcShortcutUiAllowed) return;
        ev.preventDefault();
        await this.promptNotaShippingLinesToProform(notaShipParsed);
        return;
      }
      if (!this.canCollect) return;
      const parsed = this.parseAdvanceDnD(ev);
      if (!parsed) return;
      if (parsed.kind === "ship_advance" || parsed.kind === "shipping_only") {
        ev.preventDefault();
        await this.promptAdvanceRedistribute(parsed, null);
      }
    },
    async promptAdvanceRedistribute(parsed, toShippingId, shipDropZone = "advance") {
      const dragMax = Number(parsed.maxAmount) || 0;
      if (!(dragMax > 0)) return;
      const kind = parsed.kind || "proform_only";
      const ships =
        (this.collectionStagesVisual && this.collectionStagesVisual.stage2Shippings) || [];
      const csv = this.collectionStagesVisual;
      const shipLabel =
        toShippingId != null && toShippingId !== ""
          ? (ships.find((s) => String(s.shippingId) === String(toShippingId)) || {}).name ||
            toShippingId
          : this.$t("financialSummary.stages.proformPocket");
      let effectiveMax = dragMax;
      let htmlContent = "";
      if (toShippingId != null && toShippingId !== "") {
        const shipRow = ships.find((s) => String(s.shippingId) === String(toShippingId));
        const pendingInvoice =
          shipRow != null && shipRow.pendingToCollect != null
            ? Math.max(0, Number(shipRow.pendingToCollect) || 0)
            : null;

        if (shipDropZone === "invoice") {
          /* Bloque factura embarque: asignación solo embarque (sin proforma en appliedto) — tope = pendiente de factura. */
          if (pendingInvoice != null && Number.isFinite(pendingInvoice)) {
            effectiveMax = Math.min(dragMax, pendingInvoice);
          } else {
            effectiveMax = dragMax;
          }
        } else {
          /* Zona adelanto: tope = hueco hasta la meta (también si el chip es cobro «directo» solo embarque). */
          if (shipRow && shipRow.expectedAdvanceOnShipping != null) {
            const expected = Number(shipRow.expectedAdvanceOnShipping);
            const assigned = Number(shipRow.advanceAssignedAmount) || 0;
            if (Number.isFinite(expected) && expected > 0) {
              const remainingToExpected = Math.max(0, expected - assigned);
              effectiveMax = Math.min(dragMax, remainingToExpected);
            }
          }
        }

        /*
         * El cobro da para más que el adelanto pedido en este embarque: proponer poner
         * el resto en lo que falta cobrar de su factura. El excedente sale de dos lados:
         * lo que sobra del monto arrastrado y el saldo del cobro que todavía no se aplicó
         * a nada. El usuario decide en el diálogo; nada se aplica solo.
         */
        const sameShipInternalSplit =
          kind === "shipping_only" &&
          parsed.fromShippingId != null &&
          String(parsed.fromShippingId) === String(toShippingId);
        if (shipDropZone !== "invoice" && !sameShipInternalSplit) {
          const pendingInvoice = shipRow != null && shipRow.pendingToCollect != null
            ? Math.max(0, Number(shipRow.pendingToCollect) || 0)
            : 0;
          const leftoverFromDrag = Math.max(0, dragMax - effectiveMax);
          const unapplied = Math.max(0, Number(parsed.unapplied) || 0);
          const remainderPool = leftoverFromDrag + unapplied;
          const remainderMax = Math.min(
            remainderPool,
            Math.max(0, pendingInvoice - effectiveMax)
          );
          if (remainderMax >= 0.01) {
            const split = await this.promptAdvanceRemainderSplit({
              shipLabel,
              dragMax,
              advanceMax: effectiveMax,
              remainderMax,
              pendingInvoice,
              unapplied,
            });
            if (!split) return;
            await this.postAdvanceRedistributeSplit({
              parsed,
              toShippingId,
              advanceAmount: split.advanceAmount,
              remainderAmount: split.remainderAmount,
              leftoverFromDrag,
            });
            return;
          }
        }

        if (effectiveMax < 0.01) {
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.redistributeTitle"),
            text:
              shipDropZone === "invoice"
                ? this.$tn("financialSummary.stages.redistributeNoRoomOnShipInvoice")
                : this.$tn("financialSummary.stages.redistributeNoRoomOnShip"),
          });
          return;
        }
        if (shipDropZone === "invoice") {
          const pendingLine =
            shipRow && shipRow.pendingToCollect != null
              ? "$" + this.formatNumber(Math.max(0, Number(shipRow.pendingToCollect) || 0))
              : "—";
          htmlContent = `<div class="text-start small text-muted">${this.$t(
            "financialSummary.stages.redistributeToShipInvoiceOnlyHtml",
            {
              ship: String(shipLabel),
              max: "$" + this.formatNumber(effectiveMax),
              pending: pendingLine,
            }
          )}</div>`;
        } else if (kind === "shipping_only") {
          htmlContent = `<div class="text-start small text-muted">${this.$t(
            "financialSummary.stages.redistributeToShipHtml",
            {
              ship: String(shipLabel),
              max: "$" + this.formatNumber(effectiveMax),
            }
          )}</div>`;
        } else {
          const pct =
            csv && csv.stage1 && csv.stage1.advancePct != null
              ? this.formatAdvancePct(csv.stage1.advancePct)
              : "—";
          const expectedNum =
            shipRow && shipRow.expectedAdvanceOnShipping != null
              ? Number(shipRow.expectedAdvanceOnShipping)
              : NaN;
          const assignedNum = shipRow ? Number(shipRow.advanceAssignedAmount) || 0 : 0;
          const expectedLine =
            Number.isFinite(expectedNum) && expectedNum > 0
              ? `<span style="color:#0d6efd;font-weight:800;white-space:nowrap">${pct}%</span>${this.$t(
                  "financialSummary.stages.shipExpectedAdvanceAfterPct",
                  {
                    amount: "$" + this.formatNumber(expectedNum),
                  }
                )}`
              : "—";
          htmlContent = `
<div class="text-start small mb-3 border rounded p-2 bg-light">
  <div class="fw-bold text-uppercase" style="font-size:0.65rem;letter-spacing:0.04em;color:#6c757d">${this.$t(
            "financialSummary.stages.shipStage2AdvanceBlockTitle"
          )}</div>
  <div class="text-muted text-uppercase mt-2" style="font-size:0.58rem;letter-spacing:0.03em">${this.$t(
            "financialSummary.stages.shipAdvanceMetaCaption"
          )}</div>
  <div class="mb-2">${expectedLine}</div>
  <div class="d-flex justify-content-between align-items-baseline gap-2 flex-wrap">
    <span class="text-body small">${this.$t("financialSummary.stages.shipAdvanceAssignedLabel")}</span>
    <span class="fw-semibold">${"$" + this.formatNumber(assignedNum)}</span>
  </div>
</div>
<div class="text-start small text-muted">${this.$t("financialSummary.stages.redistributeToShipHtml", {
            ship: String(shipLabel),
            max: "$" + this.formatNumber(effectiveMax),
          })}</div>`;
        }
      } else {
        const returnFromShipping = this.isRedistributeReturnFromShippingKind(kind);
        if (this.stage1HeaderDone && !returnFromShipping) {
          await Swal.fire({
            icon: "info",
            title: this.$tn("financialSummary.stages.redistributeTitle"),
            text: this.$tn("financialSummary.stages.redistributeReturnToProformBlockedAdvanceComplete"),
          });
          return;
        }
        const s1 = csv && csv.stage1;
        const expected = s1 ? Number(s1.expectedAmount) || 0 : 0;
        let pocketUsed = 0;
        if (
          s1 &&
          s1.proformaOnlyCollectedAmount != null &&
          s1.proformaOnlyCollectedAmount !== ""
        ) {
          pocketUsed = Number(s1.proformaOnlyCollectedAmount) || 0;
        } else {
          pocketUsed = (csv.stage1CollectLines || []).reduce(
            (sum, x) => sum + Number(x.totalAmount || 0),
            0
          );
        }
        const roomProformaPocket = Math.max(0, expected - pocketUsed);
        if (kind === "shipping_only") {
          const rem = s1 ? Math.max(0, Number(s1.remainingAmount) || 0) : 0;
          effectiveMax = Math.min(dragMax, roomProformaPocket, rem);
        } else {
          effectiveMax = Math.min(dragMax, roomProformaPocket);
        }
        /* Sin cupo: el backend confirma sin error (moved: 0); no bloquear el drop. */
        if (effectiveMax < 0.01) {
          await this.postAdvanceRedistribute({
            collectId: parsed.collectId,
            fromAppliedToId: parsed.fromAppliedToId,
            amount: dragMax,
            toShippingId,
          });
          return;
        }
        const remainderStays = effectiveMax < dragMax - 0.005;
        const extraNote =
          remainderStays && effectiveMax > 0.01
            ? `<div class="text-start small text-muted mt-2">${this.$t(
                "financialSummary.stages.redistributeToProformRemainderNote"
              )}</div>`
            : "";
        htmlContent = `<div class="text-start small">${this.$t(
          "financialSummary.stages.redistributeToProformHtml",
          { max: "$" + this.formatNumber(effectiveMax) }
        )}</div>${extraNote}`;
      }
      const defaultAmount = parseFloat(effectiveMax.toFixed(2));
      const { value } = await Swal.fire({
        title: this.$tn("financialSummary.stages.redistributeTitle"),
        html: htmlContent,
        input: "number",
        inputValue: String(defaultAmount),
        showCancelButton: true,
        confirmButtonText: this.$tn("financialSummary.stages.redistributeConfirm"),
        cancelButtonText: this.$tn("financialSummary.close"),
        inputAttributes: {
          step: "0.01",
          min: "0.01",
          max: String(defaultAmount),
        },
        preConfirm: (v) => {
          const n = Number(v);
          if (!Number.isFinite(n) || n < 0.01) {
            Swal.showValidationMessage(this.$tn("financialSummary.stages.redistributeInvalid"));
            return false;
          }
          if (n > effectiveMax + 0.001) {
            Swal.showValidationMessage(
              this.$tn("financialSummary.stages.redistributeTooMuch", {
                max: "$" + this.formatNumber(effectiveMax),
              })
            );
            return false;
          }
          return n;
        },
      });
      if (value === undefined || value === null) return;
      let assignmentMode;
      if (toShippingId != null && toShippingId !== "") {
        assignmentMode =
          shipDropZone === "invoice" ? "shipping_invoice_only" : "proforma_shipping";
      }
      await this.postAdvanceRedistribute({
        collectId: parsed.collectId,
        fromAppliedToId: parsed.fromAppliedToId,
        amount: Number(value),
        toShippingId,
        assignmentMode,
      });
    },
    /**
     * Diálogo cuando el cobro arrastrado supera el adelanto pedido del embarque:
     * propone repartirlo entre el adelanto y el saldo de factura del mismo embarque.
     * @returns {Promise<{advanceAmount:number, remainderAmount:number}|null>} null si se cancela
     */
    async promptAdvanceRemainderSplit({
      shipLabel,
      dragMax,
      advanceMax,
      remainderMax,
      pendingInvoice,
      unapplied = 0,
    }) {
      const advanceDefault = parseFloat((Number(advanceMax) || 0).toFixed(2));
      const remainderDefault = parseFloat((Number(remainderMax) || 0).toFixed(2));
      const money = (n) => "$" + this.formatNumber(Number(n) || 0);
      const advanceRow =
        advanceDefault >= 0.01
          ? `<div class="d-flex justify-content-between align-items-center gap-2 mb-2">
  <label class="small text-body mb-0" for="gt-split-advance">${this.$t(
    "financialSummary.stages.redistributeSplitAdvanceLabel",
    { max: money(advanceDefault) }
  )}</label>
  <input id="gt-split-advance" type="number" step="0.01" min="0" max="${advanceDefault}"
    class="swal2-input m-0" style="width:10rem" value="${advanceDefault}" />
</div>`
          : `<div class="small text-muted mb-2">${this.$t(
              "financialSummary.stages.redistributeSplitAdvanceFull"
            )}</div>`;

      const unappliedNote =
        Number(unapplied) >= 0.01
          ? `<div class="text-start small text-muted mb-2">${this.$t(
              "financialSummary.stages.redistributeSplitUnappliedNote",
              { amount: money(unapplied) }
            )}</div>`
          : "";

      const html = `
<div class="text-start small text-muted mb-3">${this.$t(
        "financialSummary.stages.redistributeSplitHint",
        {
          ship: String(shipLabel),
          total: money(dragMax),
          advanceMax: money(advanceDefault),
          pending: money(pendingInvoice),
        }
      )}</div>${unappliedNote}
<div class="text-start">
  ${advanceRow}
  <div class="form-check mb-2">
    <input class="form-check-input" type="checkbox" id="gt-split-remainder-on" checked />
    <label class="form-check-label small" for="gt-split-remainder-on">${this.$t(
      "financialSummary.stages.redistributeSplitRemainderCheckbox",
      { ship: String(shipLabel) }
    )}</label>
  </div>
  <div class="d-flex justify-content-between align-items-center gap-2">
    <label class="small text-body mb-0" for="gt-split-remainder">${this.$t(
      "financialSummary.stages.redistributeSplitRemainderLabel",
      { max: money(remainderDefault) }
    )}</label>
    <input id="gt-split-remainder" type="number" step="0.01" min="0" max="${remainderDefault}"
      class="swal2-input m-0" style="width:10rem" value="${remainderDefault}" />
  </div>
</div>`;

      const { value } = await Swal.fire({
        title: this.$tn("financialSummary.stages.redistributeTitle"),
        html,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: this.$tn("financialSummary.stages.redistributeConfirm"),
        cancelButtonText: this.$tn("financialSummary.close"),
        didOpen: () => {
          const popup = Swal.getPopup();
          const check = popup.querySelector("#gt-split-remainder-on");
          const remainderInput = popup.querySelector("#gt-split-remainder");
          if (check && remainderInput) {
            const sync = () => {
              remainderInput.disabled = !check.checked;
            };
            check.addEventListener("change", sync);
            sync();
          }
        },
        preConfirm: () => {
          const popup = Swal.getPopup();
          const advanceInput = popup.querySelector("#gt-split-advance");
          const check = popup.querySelector("#gt-split-remainder-on");
          const remainderInput = popup.querySelector("#gt-split-remainder");
          const advanceAmount = advanceInput ? Number(advanceInput.value) : 0;
          const remainderAmount =
            check && check.checked && remainderInput ? Number(remainderInput.value) : 0;
          if (!Number.isFinite(advanceAmount) || advanceAmount < 0) {
            Swal.showValidationMessage(this.$tn("financialSummary.stages.redistributeInvalid"));
            return false;
          }
          if (!Number.isFinite(remainderAmount) || remainderAmount < 0) {
            Swal.showValidationMessage(this.$tn("financialSummary.stages.redistributeInvalid"));
            return false;
          }
          if (advanceAmount > advanceDefault + 0.001) {
            Swal.showValidationMessage(
              this.$tn("financialSummary.stages.redistributeTooMuch", {
                max: money(advanceDefault),
              })
            );
            return false;
          }
          if (remainderAmount > remainderDefault + 0.001) {
            Swal.showValidationMessage(
              this.$tn("financialSummary.stages.redistributeSplitRemainderTooMuch", {
                max: money(remainderDefault),
              })
            );
            return false;
          }
          if (advanceAmount + remainderAmount < 0.01) {
            Swal.showValidationMessage(this.$tn("financialSummary.stages.redistributeInvalid"));
            return false;
          }
          return {
            advanceAmount: parseFloat(advanceAmount.toFixed(2)),
            remainderAmount: parseFloat(remainderAmount.toFixed(2)),
          };
        },
      });
      return value || null;
    },
    /**
     * Aplica el reparto propuesto: primero el tramo de adelanto (proforma+embarque) y después
     * el excedente como cobro de factura del mismo embarque. Un solo mensaje al final.
     */
    async postAdvanceRedistributeSplit({
      parsed,
      toShippingId,
      advanceAmount,
      remainderAmount,
      leftoverFromDrag = 0,
    }) {
      if (!this.itemData?._id) return;
      this.redistributeInProgress = true;
      try {
        let movedAdvance = 0;
        let movedRemainder = 0;
        if (advanceAmount >= 0.01) {
          const data = await this.sendAdvanceRedistribute({
            collectId: parsed.collectId,
            fromAppliedToId: parsed.fromAppliedToId,
            amount: advanceAmount,
            toShippingId,
            assignmentMode: "proforma_shipping",
          });
          movedAdvance = Number(data && data.moved) || 0;
        }
        if (remainderAmount >= 0.01) {
          // Una sola llamada para el excedente: lo que sobra del arrastre se mueve desde
          // su línea y el saldo sin aplicar se suma al mismo destino, en la misma fila.
          const fromDrag = Math.min(remainderAmount, Math.max(0, leftoverFromDrag));
          const fromUnapplied = Math.max(0, remainderAmount - fromDrag);
          const data = await this.sendAdvanceRedistribute({
            collectId: parsed.collectId,
            fromAppliedToId: parsed.fromAppliedToId,
            amount: fromDrag,
            fromUnapplied,
            toShippingId,
            assignmentMode: "shipping_invoice_only",
          });
          movedRemainder =
            (Number(data && data.moved) || 0) + (Number(data && data.appliedFromUnapplied) || 0);
        }
        await Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text:
            movedRemainder >= 0.01
              ? this.$tn("financialSummary.stages.redistributeSplitOk", {
                  advance: "$" + this.formatNumber(movedAdvance),
                  remainder: "$" + this.formatNumber(movedRemainder),
                })
              : this.$tn("financialSummary.stages.redistributeOk"),
          timer: 3200,
          showConfirmButton: true,
        });
        this.$emit("refresh");
      } catch (err) {
        const msg =
          (err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
          this.$t("financialSummary.stages.redistributeError");
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: String(msg) });
      } finally {
        this.redistributeInProgress = false;
      }
    },
    /** POST puro de redistribución (sin UI); devuelve el body de la respuesta. */
    async sendAdvanceRedistribute({
      collectId,
      fromAppliedToId,
      amount,
      toShippingId,
      assignmentMode,
      fromUnapplied = 0,
    }) {
      const body = { collectId, fromAppliedToId, amount };
      if (Number(fromUnapplied) > 0) body.fromUnapplied = Number(fromUnapplied);
      if (toShippingId != null && toShippingId !== "") {
        body.toShippingId = toShippingId;
      }
      if (assignmentMode === "shipping_invoice_only") {
        body.assignmentMode = "shipping_invoice_only";
      } else if (assignmentMode === "proforma_shipping") {
        body.assignmentMode = "proforma_shipping";
      }
      const { data } = await wencesApi.post(
        `/api/financial/collects/${this.itemData._id}/advance-redistribute`,
        body
      );
      return data;
    },
    async postQuickRemainingAdvance() {
      if (!this.itemData?._id || !this.canCollect) return;
      const csv = this.collectionStagesVisual;
      if (!csv || csv.stage1.remainingAmount <= 0.01) return;
      await this.openAddCollectionModal({ withContainerCalculator: true });
    },
    async postAdvanceRedistribute({
      collectId,
      fromAppliedToId,
      amount,
      toShippingId,
      assignmentMode,
    }) {
      if (!this.itemData?._id) return;
      this.redistributeInProgress = true;
      try {
        const data = await this.sendAdvanceRedistribute({
          collectId,
          fromAppliedToId,
          amount,
          toShippingId,
          assignmentMode,
        });
        const movedNum = Number(data && data.moved) || 0;
        if (
          data &&
          data.success &&
          data.noRoomInProformaPocket &&
          movedNum < 0.005
        ) {
          const reason = data.noRoomReason || "unknown";
          const keyMap = {
            no_advance_expected: "redistributeNoRoomReasonNoAdvanceExpected",
            proforma_pocket_full: "redistributeNoRoomReasonPocketFull",
            stage1_cap: "redistributeNoRoomReasonStage1Cap",
            unknown: "redistributeNoRoomNoChange",
          };
          const textKey =
            "financialSummary.stages." + (keyMap[reason] || keyMap.unknown);
          await Swal.fire({
            icon: "info",
            title: this.$tn("financialSummary.stages.redistributeTitle"),
            text: this.$tn(textKey),
          });
        } else if (data && data.success && data.cappedToProformaPocket) {
          await Swal.fire({
            icon: "info",
            title: this.$tn("success"),
            text: this.$tn("financialSummary.stages.redistributeCappedToProformaPocketToast", {
              moved: "$" + this.formatNumber(data.moved || 0),
            }),
            timer: 3200,
            showConfirmButton: true,
          });
        } else {
          await Swal.fire({
            icon: "success",
            title: this.$tn("success"),
            text: this.$tn("financialSummary.stages.redistributeOk"),
            timer: 2200,
            showConfirmButton: false,
          });
        }
        this.$emit("refresh");
      } catch (err) {
        const msg =
          (err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
          this.$t("financialSummary.stages.redistributeError");
        await Swal.fire({ icon: "error", title: this.$tn("error"), text: String(msg) });
      } finally {
        this.redistributeInProgress = false;
      }
    },
    openShipperUnitPricesModal(ship) {
      if (!ship || ship.shippingId == null) return;
      this.shipperPricesModalShippingId = String(ship.shippingId);
      this.shipperPricesModalShippingName = ship.name || "—";
      this.showShipperPricesModal = true;
    },
    closeShipperUnitPricesModal() {
      this.showShipperPricesModal = false;
      this.shipperPricesModalShippingId = "";
      this.shipperPricesModalShippingName = "";
    },
    onShipperUnitPricesSaved() {
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    openShipperInvoiceUnitQtyModal(ship) {
      if (!ship || ship.shippingId == null) return;
      this.shipperInvoiceQtyModalShippingId = String(ship.shippingId);
      this.shipperInvoiceQtyModalShippingName = ship.name || "—";
      this.showShipperInvoiceQtyModal = true;
    },
    closeShipperInvoiceUnitQtyModal() {
      this.showShipperInvoiceQtyModal = false;
      this.shipperInvoiceQtyModalShippingId = "";
      this.shipperInvoiceQtyModalShippingName = "";
    },
    onShipperInvoiceUnitQtySaved() {
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    openInvoiceUnitQtyModal(ship) {
      if (!ship || ship.shippingId == null) return;
      this.invoiceUnitQtyModalShippingId = String(ship.shippingId);
      this.invoiceUnitQtyModalShippingName = ship.name || "—";
      this.showInvoiceUnitQtyModal = true;
    },
    closeInvoiceUnitQtyModal() {
      this.showInvoiceUnitQtyModal = false;
      this.invoiceUnitQtyModalShippingId = "";
      this.invoiceUnitQtyModalShippingName = "";
    },
    onInvoiceUnitQtySaved() {
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    openAutoloadExpensesModal(ship) {
      if (!ship || ship.shippingId == null) return;
      this.autoloadExpensesModalShippingId = String(ship.shippingId);
      this.autoloadExpensesModalShippingName = ship.name || "—";
      this.showAutoloadExpensesModal = true;
    },
    closeAutoloadExpensesModal() {
      this.showAutoloadExpensesModal = false;
      this.autoloadExpensesModalShippingId = "";
      this.autoloadExpensesModalShippingName = "";
    },
    onAutoloadExpensesSaved() {
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    openAutoloadPackingListModal(ship) {
      if (!ship || ship.shippingId == null) return;
      this.autoloadPackingListModalShippingId = String(ship.shippingId);
      this.autoloadPackingListModalShippingName = ship.name || "—";
      this.showAutoloadPackingListModal = true;
    },
    closeAutoloadPackingListModal() {
      this.showAutoloadPackingListModal = false;
      this.autoloadPackingListModalShippingId = "";
      this.autoloadPackingListModalShippingName = "";
    },
    onAutoloadPackingListSaved() {
      this.$emit("refresh");
      this.$emit("shipping-saved");
    },
    extractClientId(field) {
      if (field == null || field === "") return null;
      if (typeof field === "object" && field._id != null) return String(field._id);
      return String(field);
    },
    extractClientName(field) {
      if (field && typeof field === "object" && field.name) return String(field.name);
      return "";
    },
    openCollectionStagesClientContact() {
      const id = this.collectionStagesResolvedClientId;
      if (!id) return;
      window.open(`/dynamic-model/Contact/view/${encodeURIComponent(id)}`, "_blank");
    },
    openShippingStage2SupplierContact(ship) {
      const id = this.shippingStage2SupplierContactId(ship);
      if (!id) return;
      window.open(`/dynamic-model/Contact/view/${encodeURIComponent(id)}`, "_blank");
    },
  },
};
